import React, { useState, useEffect, createContext, useContext } from 'react';
import { useLocation, useSearchParams, useParams } from 'react-router-dom';
import { Calendar, Users, MessageSquare, Clock, MapPin, Send, Check, X, Search, Globe, LogOut, User, Save, Loader, Linkedin, Building, Building2, Briefcase, Phone, Camera, GlobeIcon, Mail, Tag, Layers, Plus, Upload, ImageIcon, ChevronDown, ExternalLink, Home, ChevronUp, PlayCircle, Ticket, HeartHandshake, RefreshCw, Lock, Filter } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useEvent } from '../contexts/EventContext';
import translations from './translations.json';
import BadgeSection from './BadgeSection';
import DashboardSkeleton from './DashboardSkeleton';

// Configuration
const API_BASE_URL = '/api';

// ── Rate limiting networking (10 demandes / 10 minutes) ──────────────────────
const NET_RL_MAX    = 10;
const NET_RL_WIN_MS = 10 * 60 * 1000;

function netCheckRateLimit(senderId) {
  try {
    const raw = localStorage.getItem(`net_rl_${senderId}`);
    if (!raw) return { limited: false, retryAfter: 0 };
    const d = JSON.parse(raw);
    const elapsed = Date.now() - d.windowStart;
    if (elapsed >= NET_RL_WIN_MS) return { limited: false, retryAfter: 0 };
    if (d.count >= NET_RL_MAX) return { limited: true, retryAfter: Math.ceil((NET_RL_WIN_MS - elapsed) / 1000) };
    return { limited: false, retryAfter: 0 };
  } catch { return { limited: false, retryAfter: 0 }; }
}

function netIncrementRateLimit(senderId) {
  try {
    const raw = localStorage.getItem(`net_rl_${senderId}`);
    const now = Date.now();
    let d = raw ? JSON.parse(raw) : null;
    if (!d || (now - d.windowStart) >= NET_RL_WIN_MS) {
      d = { count: 1, windowStart: now };
    } else {
      d = { ...d, count: d.count + 1 };
    }
    localStorage.setItem(`net_rl_${senderId}`, JSON.stringify(d));
  } catch {}
}

// Context pour la langue
const LanguageContext = createContext();

const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem('language');
    if (saved) return saved;
    // Détecter la langue du navigateur
    const browserLang = (navigator.language || navigator.userLanguage || 'fr').slice(0, 2).toLowerCase();
    // Langues supportées
    if (['fr', 'en', 'de'].includes(browserLang)) return browserLang;
    return 'fr';
  });

  const changeLanguage = (lang) => {
    setLanguage(lang);
    localStorage.setItem('language', lang);
  };

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    for (const k of keys) {
      value = value?.[k];
    }
    return value || key;
  };

  return (
    <LanguageContext.Provider value={{ language, changeLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

const useLanguage = () => useContext(LanguageContext);

const MatchmakingDashboard = () => {
  const { language, changeLanguage, t } = useLanguage();
  const { user, logout, updateUser } = useAuth();
  const { event, initialized: eventInitialized, loadEventBySlug } = useEvent();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { slug: urlSlug } = useParams();

  const handleLogout = () => {
    logout();
    // Priorité : slug de l'URL → slug de l'event → /login
    const slug = urlSlug || event?.slug;
    window.location.href = slug ? `/event/${slug}/login` : '/login';
  };

  // IDs dynamiques depuis les contextes Auth et Event
  // Le PHP login renvoie idparticipant comme identifiant principal
  const currentUserId = user?.idparticipant || user?.id || null;
  const currentSubEventId = user?.sub_event_id || event?.subevent_id || event?.id || null;

  // Onglet initial depuis l'URL (?tab=requests, ?tab=meetings, etc.)
  const VALID_TABS = ['event', 'matches', 'requests', 'meetings', 'company', 'profile', 'badge', 'networking'];
  const initialTab = VALID_TABS.includes(searchParams.get('tab')) ? searchParams.get('tab') : 'event';
  const [activeTab, setActiveTab] = useState(initialTab);

  // Onglet Your Event — articles
  const [articles, setArticles] = useState([]);
  const [articlesLoading, setArticlesLoading] = useState(false);
  const [expandedArticles, setExpandedArticles] = useState({});
  // eslint-disable-next-line no-unused-vars
  const [matches, setMatches] = useState([]);
  const [companiesMatches, setCompaniesMatches] = useState([]);
  const [contentDisplay, setContentDisplay] = useState([]);
  const [meetingRequests, setMeetingRequests] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [myMeetings, setMyMeetings] = useState([]);
  const [subEvent, setSubEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadKey, setLoadKey] = useState(0); // incrémenté pour forcer un reload
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('score');          // 'score' | 'az' | 'country' | 'type'
  const [activeTagFilters, setActiveTagFilters] = useState([]);  // tags actifs (strings)
  // eslint-disable-next-line no-unused-vars
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlots, setSelectedSlots] = useState([]);

  // === MEETING REQUEST MODAL STATE ===
  const rdvModalDefault = { open: false, company: null, participants: null, participantsLoading: false, recipientId: '', message: '', sending: false, error: '', success: false };
  const [rdvModal, setRdvModal] = useState(rdvModalDefault);

  // === NETWORKING STATE ===
  const [networkingParticipants, setNetworkingParticipants] = useState([]);
  const [networkingLoading, setNetworkingLoading] = useState(false);
  const [networkingSearch, setNetworkingSearch] = useState('');
  const [networkingConnections, setNetworkingConnections] = useState([]);
  const [networkingActionLoading, setNetworkingActionLoading] = useState({});
  const [networkingSubTab, setNetworkingSubTab] = useState('participants');
  const [netRateLimitModal, setNetRateLimitModal] = useState({ open: false, retryAfter: 0 });
  const [netRateLimitCountdown, setNetRateLimitCountdown] = useState(0);
  const [netSelectedCategories, setNetSelectedCategories]   = useState([]);
  const [netSelectedRequests, setNetSelectedRequests]       = useState([]);
  const [netSelectedCompanyType, setNetSelectedCompanyType] = useState('');
  const [netSortField, setNetSortField]                     = useState('default');
  const [netFiltersOpen, setNetFiltersOpen]                 = useState(false);
  const [netProfileModal, setNetProfileModal]               = useState(null);

  // === MEETING REQUESTS FILTER / SEARCH STATE ===
  const [requestStatusFilter, setRequestStatusFilter] = useState([]); // [] = tous
  const [requestSearchTerm, setRequestSearchTerm] = useState('');
  const [requestsLoading, setRequestsLoading] = useState(false);

  // === MES RENDEZ-VOUS FILTER / SEARCH STATE ===
  const [meetingsDayFilter, setMeetingsDayFilter] = useState(null);
  const [meetingsSearchTerm, setMeetingsSearchTerm] = useState('');
  const [meetingsStatusFilter, setMeetingsStatusFilter] = useState(null);
  const [sendAgendaState, setSendAgendaState] = useState('idle'); // 'idle'|'sending'|'sent'|'error'

  // === CANCEL MEETING MODAL ===
  const cancelModalDefault = { open: false, requestId: null, message: '', loading: false };
  const [cancelModal, setCancelModal] = useState(cancelModalDefault);

  // === PROFILE STATE ===
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [profileSuccess, setProfileSuccess] = useState('');
  const [profileForm, setProfileForm] = useState({});
  const [photoUploading, setPhotoUploading] = useState(false);

  // === COMPANY STATE ===
  const [companyData, setCompanyData] = useState(null);
  const [companyForm, setCompanyForm] = useState({});
  const [companyLoading, setCompanyLoading] = useState(false);
  const [companyLoadKey, setCompanyLoadKey] = useState(0); // incrémenter pour forcer un rechargement
  const [profileLoadKey, setProfileLoadKey] = useState(0); // incrémenter pour recharger le profil
  const [companySaving, setCompanySaving] = useState(false);
  const [companyError, setCompanyError] = useState('');
  const [companySuccess, setCompanySuccess] = useState('');
  const [categories, setCategories] = useState([]);
  const [activities, setActivities] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedActivities, setSelectedActivities] = useState([]);
  const [countries, setCountries] = useState([]);
  const [types, setTypes] = useState([]);
  const [selectedCountryId, setSelectedCountryId] = useState('');
  const [selectedTypeId, setSelectedTypeId] = useState(''); // ID du linked record Types
  const [requests, setRequests] = useState([]);
  const [selectedRequests, setSelectedRequests] = useState([]);
  const [logoUploading, setLogoUploading] = useState(false);

  // === ACCOUNT DELETION STATE ===
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [accountDeleting, setAccountDeleting] = useState(false);
  const [accountDeleteError, setAccountDeleteError] = useState('');

  // === PLANNING STATE ===
  const [allSlots, setAllSlots] = useState([]);
  const [userAvailability, setUserAvailability] = useState([]);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [availabilityToggling, setAvailabilityToggling] = useState({});
  const [planningDay, setPlanningDay] = useState(1);
  // Sous-onglet planning depuis ?subtab= (ex: ?tab=meetings&subtab=availability)
  const initialSubTab = searchParams.get('subtab') === 'availability' ? 'availability' : 'meetings';
  const [planningSubTab, setPlanningSubTab] = useState(initialSubTab); // 'meetings' | 'availability'

  // Lazy-load des participants networking + connexions quand l'onglet devient actif
  useEffect(() => {
    if (activeTab !== 'networking' || !currentSubEventId || networkingParticipants.length > 0 || networkingLoading) return;
    const myId = profile?.record_id;
    setNetworkingLoading(true);
    Promise.all([
      fetch(`${API_BASE_URL}/networking-participants/${currentSubEventId}${myId ? `?me=${myId}` : ''}`).then(r => r.json()),
      myId ? fetch(`${API_BASE_URL}/networking-connections/${currentSubEventId}?me=${myId}`).then(r => r.json()) : Promise.resolve({ success: true, data: [] }),
    ])
      .then(([partData, connData]) => {
        if (partData.success) setNetworkingParticipants(partData.data);
        if (connData.success) setNetworkingConnections(connData.data);
      })
      .catch(() => {})
      .finally(() => setNetworkingLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, currentSubEventId]);

  const getNetConnState = (participantId) => {
    const myId = profile?.record_id;
    if (!myId) return null;
    const conn = networkingConnections.find(c =>
      (c.sender_id === myId && c.receiver_id === participantId) ||
      (c.receiver_id === myId && c.sender_id === participantId)
    );
    if (!conn) return null;
    const type = conn.status === 'on' ? 'connected' : conn.status === 'decline' ? 'declined' : conn.sender_id === myId ? 'pending_sent' : 'pending_received';
    return { type, connectionId: conn.id };
  };

  const handleNetworkingConnect = async (participantId) => {
    const myId = profile?.record_id;
    if (!myId) return;
    // Check localStorage avant l'appel réseau
    const rl = netCheckRateLimit(myId);
    if (rl.limited) { setNetRateLimitModal({ open: true, retryAfter: rl.retryAfter }); return; }
    setNetworkingActionLoading(prev => ({ ...prev, [participantId]: 'connect' }));
    try {
      const res  = await fetch(`${API_BASE_URL}/networking-connect`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sender_id: myId, receiver_id: participantId, subevent_id: currentSubEventId }),
      });
      const data = await res.json();
      if (data.rate_limited) {
        setNetRateLimitModal({ open: true, retryAfter: data.retry_after || 600 });
      } else if (data.success) {
        if (!data.already_exists) netIncrementRateLimit(myId);
        setNetworkingConnections(prev => [
          ...prev.filter(c => !(c.sender_id === myId && c.receiver_id === participantId)),
          { id: data.connection_id, status: 'Pending', sender_id: myId, receiver_id: participantId },
        ]);
      }
    } catch {}
    setNetworkingActionLoading(prev => ({ ...prev, [participantId]: null }));
  };

  const handleNetworkingRespond = async (connectionId, status, participantId) => {
    setNetworkingActionLoading(prev => ({ ...prev, [participantId]: status }));
    try {
      const res  = await fetch(`${API_BASE_URL}/networking-respond/${connectionId}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      const data = await res.json();
      if (data.success) {
        setNetworkingConnections(prev => prev.map(c => c.id === connectionId ? { ...c, status } : c));
        if (status === 'on') setNetworkingSubTab('connected');
      }
    } catch {}
    setNetworkingActionLoading(prev => ({ ...prev, [participantId]: null }));
  };

  // Rafraîchir l'event au montage pour que les URLs Airtable (logo, etc.) ne soient jamais expirées
  useEffect(() => {
    const slug = urlSlug || event?.slug;
    if (slug) loadEventBySlug(slug);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // === WELCOME POPUP (nouveau participant) ===
  const [showWelcomePopup, setShowWelcomePopup] = useState(false);
  useEffect(() => {
    if (location.state?.newUser) {
      setShowWelcomePopup(true);
      // Nettoyer le state pour éviter de ré-afficher au rechargement
      window.history.replaceState({}, document.title);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!netRateLimitModal.open) return;
    setNetRateLimitCountdown(netRateLimitModal.retryAfter);
    const iv = setInterval(() => {
      setNetRateLimitCountdown(prev => {
        if (prev <= 1) { clearInterval(iv); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(iv);
  }, [netRateLimitModal.open, netRateLimitModal.retryAfter]);

  // Codes pays européens (pour tri prioritaire)
  const EUROPEAN_CODES = new Set([
    'AL','AD','AT','BY','BE','BA','BG','HR','CY','CZ','DK','EE','FI','FR',
    'DE','GR','HU','IS','IE','IT','XK','LV','LI','LT','LU','MT','MD','MC',
    'ME','NL','MK','NO','PL','PT','RO','RU','SM','RS','SK','SI','ES','SE',
    'CH','TR','UA','GB','VA'
  ]);

  // Charger les données (matches, requests, meetings) une fois le profil chargé
  useEffect(() => {
    if (!currentSubEventId) {
      // Attendre que l'EventContext ait fini de lire localStorage avant de conclure à une erreur
      if (!eventInitialized) return; // Hydratation en cours → garder le spinner
      setLoading(false);
      setError('no_event'); // Erreur réelle : toujours pas d'événement après hydratation
      return;
    }
    // Attendre que le profil soit chargé pour avoir le record_id Airtable
    if (!profile?.record_id) return;

    const userRecordId = profile.record_id;

    // Helper : fetch sécurisé — ne lance jamais d'exception, log les erreurs
    const safeFetch = async (url, label) => {
      try {
        const res = await fetch(url);
        if (!res.ok) { console.warn(`[${label}] HTTP ${res.status}`); return null; }
        const data = await res.json();
        return data;
      } catch (err) {
        console.warn(`[${label}] fetch failed:`, err.message);
        return null;
      }
    };

    const loadData = async () => {
      setLoading(true);
      setError(null);

      // Endpoint agrégé : 1 requête HTTP au lieu de 6 (données servies depuis cache APCu)
      const dashData = await safeFetch(
        `${API_BASE_URL}/dashboard/${currentSubEventId}?me=${userRecordId}`,
        'dashboard'
      );

      if (dashData?.success) {
        if (dashData.subevent?.success)        setSubEvent(dashData.subevent.data);
        if (dashData.participants?.success)    setMatches(dashData.participants.data);
        if (dashData.requests?.success)        setMeetingRequests(dashData.requests.data);
        if (dashData.meetings?.success)        setMyMeetings(dashData.meetings.data);
        if (dashData.slots?.success)           setAvailableSlots(dashData.slots.data);
        if (dashData.content_display?.success) setContentDisplay(dashData.content_display.data);
      } else {
        // Fallback : appels individuels si l'endpoint agrégé échoue
        const [subEventData, matchesData, requestsData, meetingsData, slotsData, contentDisplayData] = await Promise.all([
          safeFetch(`${API_BASE_URL}/subevent/${currentSubEventId}`,                        'subevent'),
          safeFetch(`${API_BASE_URL}/participants/${currentSubEventId}?me=${userRecordId}`, 'participants'),
          safeFetch(`${API_BASE_URL}/requests/${userRecordId}`,                             'requests'),
          safeFetch(`${API_BASE_URL}/meetings/${userRecordId}`,                             'meetings'),
          safeFetch(`${API_BASE_URL}/slots/${currentSubEventId}`,                           'slots'),
          safeFetch(`${API_BASE_URL}/content-display/${currentSubEventId}`,                'content-display'),
        ]);
        if (subEventData?.success)       setSubEvent(subEventData.data);
        if (matchesData?.success)        setMatches(matchesData.data);
        if (requestsData?.success)       setMeetingRequests(requestsData.data);
        if (meetingsData?.success)       setMyMeetings(meetingsData.data);
        if (slotsData?.success)          setAvailableSlots(slotsData.data);
        if (contentDisplayData?.success) setContentDisplay(contentDisplayData.data);
      }

      setLoading(false);
    };

    loadData();
  }, [profile?.record_id, currentSubEventId, eventInitialized, loadKey]); // eslint-disable-line

  // Polling périodique pour contentDisplay (60s) — détecte les changements orga sans reload manuel
  useEffect(() => {
    if (!currentSubEventId) return;
    const interval = setInterval(async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/content-display/${currentSubEventId}`);
        if (!res.ok) return;
        const data = await res.json();
        if (data?.success) setContentDisplay(data.data);
      } catch { /* silencieux */ }
    }, 60000);
    return () => clearInterval(interval);
  }, [currentSubEventId]); // eslint-disable-line

  // Redirige vers le premier onglet visible si l'onglet actif devient caché
  useEffect(() => {
    if (!contentDisplay.length) return;
    const hiddenKeys = new Set(
      contentDisplay.filter(c => c.status === 'off').map(c => ({
        Articles: 'event', Matches: 'matches', Networking: 'networking', Badge: 'badge', Request: 'requests', Planning: 'meetings',
      }[c.type_id])).filter(Boolean)
    );
    if (hiddenKeys.has(activeTab)) {
      const fallback = ['event', 'matches', 'requests', 'meetings', 'networking', 'company', 'badge']
        .find(k => !hiddenKeys.has(k));
      if (fallback) setActiveTab(fallback);
    }
  }, [contentDisplay]); // eslint-disable-line

  // Refresh manuel : bypasse le cache content-display (nocache=1) puis recharge tout
  const handleManualRefresh = async () => {
    if (!currentSubEventId) { setLoadKey(k => k + 1); return; }
    try {
      const res = await fetch(`${API_BASE_URL}/content-display/${currentSubEventId}?nocache=1`);
      const data = await res.json();
      if (data?.success) setContentDisplay(data.data);
    } catch {}
    setLoadKey(k => k + 1);
  };

  // ── Matching client-side ──────────────────────────────────────────────────
  // Noms des activités et demandes de la company de l'utilisateur (pour le scoring)
  const userActivityNames = selectedActivities
    .map(id => activities.find(a => a.id === id)?.name)
    .filter(Boolean);

  const userRequestNames = selectedRequests
    .map(id => requests.find(r => r.id === id)?.name)
    .filter(Boolean);

  // Tags cliquables : toutes les activités + demandes de la company du user
  const userTags = [
    ...userActivityNames.map(name => ({ name, type: 'activity' })),
    ...userRequestNames.map(name => ({ name, type: 'request'  })),
  ];

  // Score de compatibilité : nombre d'activités + demandes en commun
  const scoredCompanies = companiesMatches.map(c => {
    const matchedActivities = (c.activity_names || []).filter(a =>
      userActivityNames.some(ua => ua.toLowerCase() === a.toLowerCase())
    );
    const matchedRequests = (c.request_names || []).filter(r =>
      userRequestNames.some(ur => ur.toLowerCase() === r.toLowerCase())
    );
    return {
      ...c,
      matchScore:       matchedActivities.length + matchedRequests.length,
      matchedActivities,
      matchedRequests,
    };
  });

  // Filtrage par recherche textuelle + tags actifs, puis tri
  const filteredAndSorted = scoredCompanies
    .filter(company => {
      const term = searchTerm.toLowerCase();
      const textOk = !term ||
        (company.name        || '').toLowerCase().includes(term) ||
        (company.country     || '').toLowerCase().includes(term) ||
        (company.type_name   || '').toLowerCase().includes(term) ||
        (company.activity_names || []).some(a => a.toLowerCase().includes(term)) ||
        (company.requests       || []).some(r => r.name.toLowerCase().includes(term));
      // Au moins un des tags actifs doit être présent dans les activités ou demandes
      const tagOk = activeTagFilters.length === 0 ||
        activeTagFilters.some(tag =>
          (company.activity_names || []).some(a => a.toLowerCase() === tag.toLowerCase()) ||
          (company.request_names  || []).some(r => r.toLowerCase() === tag.toLowerCase())
        );
      return textOk && tagOk;
    })
    .sort((a, b) => {
      if (sortBy === 'score')   return (b.matchScore || 0) - (a.matchScore || 0);
      if (sortBy === 'az')      return (a.name        || '').localeCompare(b.name        || '');
      if (sortBy === 'country') return (a.country     || '').localeCompare(b.country     || '');
      if (sortBy === 'type')    return (a.type_name   || '').localeCompare(b.type_name   || '');
      return 0;
    });

  const handleSendRequest = async () => {
    if (selectedSlots.length === 0) {
      alert(t('request_modal.select_slots_error') || 'Veuillez sélectionner au moins un créneau');
      return;
    }

    const message = document.getElementById('request-message').value;

    try {
      const response = await fetch(`${API_BASE_URL}/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          match_id: selectedMatch.match?.match_id || null,
          requester_id: profile?.record_id || currentUserId,
          recipient_id: selectedMatch.id,
          subevent_id: currentSubEventId,
          slot_ids: selectedSlots,
          message: message
        })
      });

      const data = await response.json();
      if (data.success) {
        alert(t('request_modal.request_sent'));
        setShowRequestModal(false);
        setSelectedSlots([]);
        // Recharger les données
        window.location.reload();
      }
    } catch (error) {
      console.error('Error sending request:', error);
      alert('Erreur lors de l\'envoi de la demande');
    }
  };

  const handleRespondToRequest = async (requestId, accepted, slotId = null) => {
    try {
      const response = await fetch(`${API_BASE_URL}/respond`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          request_id: requestId,
          accepted: accepted,
          response_message: accepted ? 'Accepted' : 'Declined',
          selected_slot_id: slotId
        })
      });

      const data = await response.json();
      if (data.success) {
        alert(accepted ? t('request_card.accepted') : t('request_card.declined'));
        window.location.reload();
      }
    } catch (error) {
      console.error('Error responding to request:', error);
      alert('Erreur lors de la réponse');
    }
  };

  const toggleSlotSelection = (slotId) => {
    if (selectedSlots.includes(slotId)) {
      setSelectedSlots(selectedSlots.filter(id => id !== slotId));
    } else {
      if (selectedSlots.length < 3) {
        setSelectedSlots([...selectedSlots, slotId]);
      } else {
        alert(t('request_modal.max_slots') || 'Maximum 3 créneaux');
      }
    }
  };

  // Charger le profil au démarrage du dashboard (en tâche de fond)
  useEffect(() => {
    if (!user?.email || !currentSubEventId) return;
    if (profile) return; // Déjà chargé

    const loadProfile = async () => {
      setProfileLoading(true);
      setProfileError('');
      try {
        const res = await fetch(`${API_BASE_URL}/profile?email=${encodeURIComponent(user.email)}&subevent=${encodeURIComponent(currentSubEventId)}`);
        const data = await res.json();
        if (data.success && data.profile) {
          setProfile(data.profile);
          setProfileForm({
            firstname: data.profile.firstname || '',
            lastname: data.profile.lastname || '',
            // company vient du lookup 'company (from Companies)' — non éditable ici
            fonction: data.profile.fonction || '',
            description: data.profile.description || '',
            country: data.profile.country || '',
            linkedin: data.profile.linkedin || '',
            phone: data.profile.phone || ''
          });
          // Synchroniser le header avec le prénom/nom du profil Airtable (App-Users)
          // Met à jour le state React ET localStorage via updateUser
          if (data.profile.firstname || data.profile.lastname) {
            updateUser({
              firstname: data.profile.firstname || '',
              lastname:  data.profile.lastname  || '',
            });
          }
        } else {
          setProfileError(t('profile.not_found'));
        }
      } catch (err) {
        console.error('Error loading profile:', err);
        setProfileError(t('profile.load_error'));
      } finally {
        setProfileLoading(false);
      }
    };

    loadProfile();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email, currentSubEventId, profileLoadKey]);

  // Charger la company et les categories/activities (dès que email + subevent sont dispo, ou après création)
  useEffect(() => {
    if (!user?.email || !currentSubEventId) return;

    // Helper : parse JSON en sécurité, retourne null si erreur
    const safeJson = async (res, label) => {
      try {
        if (!res.ok) { console.warn(`[${label}] HTTP ${res.status}`); return null; }
        return await res.json();
      } catch (e) {
        console.warn(`[${label}] JSON parse error:`, e.message);
        return null;
      }
    };

    const loadCompanyData = async () => {
      setCompanyLoading(true);
      try {
        // Charger categories, activities, countries, types et requests en parallèle avec la company
        const [companyRes, catRes, actRes, countriesRes, typesRes, requestsRes] = await Promise.all([
          fetch(`${API_BASE_URL}/company-by-user/${encodeURIComponent(user.email)}?subevent=${encodeURIComponent(currentSubEventId)}`),
          fetch(`${API_BASE_URL}/categories/${currentSubEventId}`),
          fetch(`${API_BASE_URL}/activities/${currentSubEventId}`),
          fetch(`${API_BASE_URL}/countries`),
          fetch(`${API_BASE_URL}/types?subevent_id=${encodeURIComponent(currentSubEventId)}`),
          fetch(`${API_BASE_URL}/requests?subevent=${encodeURIComponent(currentSubEventId)}`)
        ]);

        const catData      = await safeJson(catRes,      'categories');
        const actData      = await safeJson(actRes,      'activities');
        const countriesData= await safeJson(countriesRes,'countries');
        const typesData    = await safeJson(typesRes,    'types');
        const requestsData = await safeJson(requestsRes, 'requests');
        const compData     = await safeJson(companyRes,  'company');

        if (catData?.success)       setCategories(catData.data);
        if (actData?.success)       setActivities(actData.data);
        if (countriesData?.success) setCountries(countriesData.data);
        if (typesData?.success)     setTypes(typesData.data);
        if (requestsData?.success)  setRequests(requestsData.data);
        if (compData?.success && compData.data) {
          const c = compData.data;
          setCompanyData(c);
          setCompanyForm({
            company: c.company || '',
            description: c.description || '',
            address: c.address || '',
            zipcode: c.zipcode || '',
            city: c.city || '',
            email: c.email || '',
            phone: c.phone || '',
            website: c.website || '',
            booth: c.booth || '',
            linkedin: c.linkedin || ''
          });
          setSelectedCountryId(c.country_id || '');
          setSelectedTypeId(c.type_id || '');
          setSelectedCategories(c.category_ids || []);
          setSelectedActivities(c.activity_ids || []);
          // Les demandes sont portées par Request.companies → calculer à partir du résultat /api/requests
          const linkedRequests = (requestsData?.data || [])
            .filter(r => (r.companies || []).includes(c.record_id))
            .map(r => r.id);
          setSelectedRequests(linkedRequests);
        } else {
          setCompanyData(false); // Pas de company trouvée
        }

        // Charger la liste des autres companies du sub-event (vue Mes Matches)
        // On exclut la company de l'utilisateur si elle existe
        try {
          const myCompanyId = compData?.success && compData.data ? compData.data.record_id : null;
          const listUrl = `${API_BASE_URL}/companies-list/${currentSubEventId}${myCompanyId ? `?exclude=${myCompanyId}` : ''}`;
          const listRes = await fetch(listUrl);
          if (listRes.ok) {
            const listData = await listRes.json();
            if (listData.success) {
              // Le PHP retourne déjà country_name et request_names via les lookup fields Airtable
              // Pas besoin de résolution client-side
              const enriched = listData.data.map(c => ({
                ...c,
                // Alias pour la compatibilité avec CompanyMatchCard
                country:  c.country_name || '',
                requests: (c.request_names || []).map((name, idx) => ({
                  id:   c.request_ids?.[idx] || name,
                  name,
                })),
              }));
              setCompaniesMatches(enriched);
            }
          }
        } catch (listErr) {
          console.warn('companies-list fetch failed (non-fatal):', listErr);
        }

      } catch (err) {
        console.error('Error loading company:', err);
        setCompanyData(false);     // Évite un spinner infini
        setCompanyError(err.message || 'Erreur de chargement');
      } finally {
        setCompanyLoading(false);
      }
    };

    loadCompanyData();
  }, [user?.email, currentSubEventId, companyLoadKey]);

  // Auto-link à une company existante si pending_company_id en localStorage
  // (cas multi-utilisateurs sur un même code d'accès)
  useEffect(() => {
    if (companyData !== false) return; // Seulement si aucune company trouvée
    const pendingCompanyId = localStorage.getItem('pending_company_id');
    const airtableUserId   = user?.airtable_record_id || profile?.record_id;
    if (!pendingCompanyId || !airtableUserId) return;

    const doAutoLink = async () => {
      try {
        const res = await fetch('/auth/link-user-company.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ airtable_user_id: airtableUserId, company_id: pendingCompanyId }),
        });
        if (res.ok) {
          localStorage.removeItem('pending_company_id');
          setCompanyLoadKey(k => k + 1); // Recharger la company nouvellement liée
        } else {
          console.warn('[Dashboard] Auto-link company failed:', await res.text());
        }
      } catch (e) {
        console.warn('[Dashboard] Auto-link company error:', e);
      }
    };
    doAutoLink();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyData]);

  // Charger créneaux + disponibilités quand l'onglet planning est ouvert
  useEffect(() => {
    if (activeTab !== 'meetings' || !profile?.record_id || !currentSubEventId) return;
    const loadPlanning = async () => {
      setAvailabilityLoading(true);
      try {
        const [slotsRes, avRes] = await Promise.all([
          fetch(`${API_BASE_URL}/slots-all/${currentSubEventId}`),
          fetch(`${API_BASE_URL}/availability/${profile.record_id}?subevent=${currentSubEventId}`),
        ]);
        const slotsData = await slotsRes.json();
        const avData    = await avRes.json();
        if (slotsData?.success) setAllSlots(slotsData.data || []);
        if (avData?.success)    setUserAvailability(avData.data || []);
      } catch (err) {
        console.error('[planning] load error:', err);
      }
      setAvailabilityLoading(false);
    };
    loadPlanning();
  }, [activeTab, profile?.record_id, currentSubEventId]);

  // ── Charger les meeting requests (appelable à la demande ou via useEffect) ──────────
  // Charge les articles de l'événement (type "info", triés par Position)
  const loadArticles = async () => {
    if (!currentSubEventId) return;
    setArticlesLoading(true);
    try {
      const res  = await fetch(`${API_BASE_URL}/articles/${currentSubEventId}`);
      const data = await res.json();
      if (data.success && data.data) {
        setArticles(data.data);
        // Expand le premier article par défaut
        if (data.data.length > 0) {
          setExpandedArticles({ [data.data[0].id]: true });
        }
      }
    } catch (err) {
      console.warn('[loadArticles]', err);
    } finally {
      setArticlesLoading(false);
    }
  };

  const loadMeetingRequests = async () => {
    if (!profile?.record_id || !currentSubEventId) return;
    setRequestsLoading(true);
    try {
      const res  = await fetch(`${API_BASE_URL}/meeting-requests?user_id=${profile.record_id}&subevent=${currentSubEventId}`);
      const data = await res.json();
      if (data.success) setMeetingRequests(data.data || []);
    } catch (err) {
      console.error('[meeting-requests load]', err);
    }
    setRequestsLoading(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadMeetingRequests(); }, [profile?.record_id, currentSubEventId]);

  // Charge les articles si l'onglet event est actif au démarrage ou quand le subevent est prêt
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (activeTab === 'event' && currentSubEventId) loadArticles(); }, [currentSubEventId]);

  // ── Ouvrir la modale RDV pour une société ───────────────────────────────────────────
  const openRdvModal = async (company) => {
    setRdvModal({ open: true, company, participants: null, participantsLoading: true, recipientId: '', message: '', sending: false, error: '', success: false });
    try {
      const userIds = (company.users || []).join(',');
      const res  = await fetch(`${API_BASE_URL}/company-user?ids=${encodeURIComponent(userIds)}`);
      const data = await res.json();
      const parts = data.success ? data.data : [];
      setRdvModal(prev => ({
        ...prev,
        participants:        parts,
        participantsLoading: false,
        recipientId:         parts.length === 1 ? parts[0].id : '',
      }));
    } catch {
      setRdvModal(prev => ({ ...prev, participants: [], participantsLoading: false }));
    }
  };

  // ── Envoyer la demande de RDV ────────────────────────────────────────────────────────
  const sendMeetingRequest = async () => {
    if (!rdvModal.recipientId) { setRdvModal(prev => ({ ...prev, error: t('request.error_recipient') })); return; }
    if (!rdvModal.message.trim()) { setRdvModal(prev => ({ ...prev, error: t('request.error_message') })); return; }
    setRdvModal(prev => ({ ...prev, sending: true, error: '' }));
    try {
      const res  = await fetch(`${API_BASE_URL}/meeting-requests`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({
          requester_id: profile.record_id,
          recipient_id: rdvModal.recipientId,
          sub_event_id: currentSubEventId,
          message:      rdvModal.message,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setMeetingRequests(prev => [data.data, ...prev]);
        setRdvModal(prev => ({ ...prev, sending: false, success: true }));
        setTimeout(() => setRdvModal({ open: false, company: null, participants: null, participantsLoading: false, recipientId: '', message: '', sending: false, error: '', success: false }), 1800);
      } else {
        setRdvModal(prev => ({ ...prev, sending: false, error: data.error || t('request.send_error') }));
      }
    } catch (err) {
      setRdvModal(prev => ({ ...prev, sending: false, error: err.message }));
    }
  };

  // ── Annuler / Accepter / Refuser une demande ─────────────────────────────────────────
  const updateMeetingRequestStatus = async (requestId, status, message = '') => {
    try {
      const body = { status };
      if (message) body.response_message = message;
      const res = await fetch(`${API_BASE_URL}/meeting-requests/${requestId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.success) {
        // Utiliser le statut réel retourné par le PHP (peut être 'Confirmed' si auto-assign réussi)
        const actualStatus = data.data?.status || status;
        setMeetingRequests(prev => prev.map(r =>
          r.id === requestId ? { ...r, status: actualStatus, response_message: message || r.response_message } : r
        ));
        // Re-fetch complet pour récupérer slot/table si la demande devient Confirmed
        if (actualStatus === 'Confirmed') loadMeetingRequests();
      }
    } catch (err) {
      console.error('[meeting-request status update]', err);
    }
  };

  const handleSendMyAgenda = async () => {
    setSendAgendaState('sending');
    const confirmedMeetings = meetingRequests.filter(r => r.status === 'Confirmed');
    const payload = {
      participant_name:     [profile?.firstname, profile?.lastname].filter(Boolean).join(' ') || user?.firstname || '',
      participant_email:    profile?.email || user?.email || '',
      participant_company:  profile?.company_name_from_companies || profile?.company || '',
      participant_fonction: profile?.fonction || '',
      event_name:           event?.name || subEvent?.name || '',
      sub_event_id:         currentSubEventId || '',
      slots_total:          allSlots.length,
      meetings_count:       confirmedMeetings.length,
      meetings: confirmedMeetings.map(r => {
        const isSent = r.requester_id === profile?.record_id;
        return {
          slot_time:     r.confirmed_slot_start ? `${r.confirmed_slot_start}–${r.confirmed_slot_end || ''}` : '',
          table:         r.table_label || '',
          other_name:    isSent ? r.recipient_name    : r.requester_name,
          other_company: isSent ? r.recipient_company : r.requester_company,
          other_role:    isSent ? r.recipient_role    : r.requester_role,
        };
      }),
    };
    try {
      const res = await fetch(`${API_BASE_URL}/send-agenda`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error();
      setSendAgendaState('sent');
      setTimeout(() => setSendAgendaState('idle'), 4000);
    } catch {
      setSendAgendaState('error');
      setTimeout(() => setSendAgendaState('idle'), 4000);
    }
  };

  const handleCompanyChange = (field, value) => {
    setCompanyForm(prev => ({ ...prev, [field]: value }));
    setCompanySuccess('');
  };

  const handleCompanySave = async () => {
    if (!companyData?.record_id) return;
    setCompanySaving(true);
    setCompanyError('');
    setCompanySuccess('');

    try {
      const res = await fetch(`${API_BASE_URL}/company/${companyData.record_id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...companyForm,
          type_id: selectedTypeId || '',
          country_ids: selectedCountryId ? [selectedCountryId] : [],
          category_ids: selectedCategories,
          activity_ids: selectedActivities,
          request_ids: selectedRequests
        })
      });
      const data = await res.json();
      if (data.success) {
        setCompanySuccess(t('company.save_success'));
        // Mise à jour optimiste du profil : affiche le nouveau nom de société immédiatement
        if (companyForm.company) {
          setProfile(prev => prev ? { ...prev, company_name_from_companies: companyForm.company } : prev);
        }
        setProfileLoadKey(k => k + 1); // Recharger le profil pour mettre à jour le champ Entreprise
      } else {
        setCompanyError(data.error || t('company.save_error'));
      }
    } catch (err) {
      console.error('Error saving company:', err);
      setCompanyError(t('company.save_error'));
    } finally {
      setCompanySaving(false);
    }
  };

  const handleCreateCompany = async () => {
    setCompanySaving(true);
    setCompanyError('');
    try {
      const res = await fetch(`${API_BASE_URL}/company`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company: profile?.company || user?.company || '',
          users: [profile.record_id],
          sub_event: currentSubEventId,
          email: profile?.email || user?.email || ''
        })
      });
      const data = await res.json();
      if (data.success) {
        // Réinitialiser et forcer un rechargement complet
        setCompanyData(null);
        setCompanyForm({});
        setSelectedCategories([]);
        setSelectedActivities([]);
        setSelectedRequests([]);
        // Mise à jour optimiste du profil : affiche le nom de société immédiatement sans attendre Airtable
        const newCompanyName = companyForm.company || profile?.company || user?.company || '';
        if (newCompanyName) {
          setProfile(prev => prev ? { ...prev, company_name_from_companies: newCompanyName } : prev);
        }
        setCompanyLoadKey(k => k + 1); // déclenche le useEffect
        setProfileLoadKey(k => k + 1); // Recharger le profil pour mettre à jour le champ Entreprise
      } else {
        setCompanyError(data.error || t('company.save_error'));
      }
    } catch (err) {
      console.error('Error creating company:', err);
      setCompanyError(t('company.save_error'));
    } finally {
      setCompanySaving(false);
    }
  };

  // Toggle disponibilité : Disponible (pas de record) ↔ Bloqué (record Blocked)
  // Modèle "pas de record = Disponible" : seuls les créneaux bloqués sont stockés.
  const toggleAvailability = async (slot, availabilityBySlot) => {
    if (availabilityToggling[slot.id]) return;
    const rec = availabilityBySlot[slot.id];
    setAvailabilityToggling(prev => ({ ...prev, [slot.id]: true }));
    try {
      if (!rec || rec.status === 'Available') {
        // Disponible (pas de record ou ancien record Available) → Bloqué
        if (!rec) {
          // Pas de record → créer Blocked directement
          const res = await fetch(`${API_BASE_URL}/availability`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ user_id: profile.record_id, slot_id: slot.id, subevent_id: currentSubEventId, status: 'Blocked' }),
          });
          const data = await res.json();
          if (data.success && data.record) setUserAvailability(prev => [...prev, data.record]);
        } else {
          // Ancien record Available → PATCH Blocked (rétrocompatibilité)
          const res = await fetch(`${API_BASE_URL}/availability/${rec.id}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ status: 'Blocked' }),
          });
          const data = await res.json();
          if (data.success) setUserAvailability(prev => prev.map(a => a.id === rec.id ? { ...a, status: 'Blocked' } : a));
        }
      } else {
        // Bloqué → supprimer (retour à Disponible implicite)
        await fetch(`${API_BASE_URL}/availability/${rec.id}`, { method: 'DELETE' });
        setUserAvailability(prev => prev.filter(a => a.id !== rec.id));
      }
    } catch (err) {
      console.error('[availability] toggle error:', err);
    }
    setAvailabilityToggling(prev => ({ ...prev, [slot.id]: false }));
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !companyData?.record_id) return;
    setLogoUploading(true);
    setCompanyError('');
    try {
      // Comprimer (800px JPEG 75%) puis encoder en base64
      const compressed = await compressImage(file);
      const base64     = await blobToBase64(compressed);

      // Envoi JSON — contourne les limites multipart de Nginx/PHP
      const res = await fetch(`${API_BASE_URL}/company/${companyData.record_id}/logo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ file: base64, filename: 'logo.jpg', contentType: 'image/jpeg' })
      });
      const data = await res.json();
      if (data.success) {
        setCompanyData(prev => ({ ...prev, logo: data.logo_url, logo_thumbnail: data.logo_thumbnail }));
        setCompanySuccess(language === 'fr' ? 'Logo mis à jour !' : language === 'de' ? 'Logo aktualisiert!' : 'Logo updated!');
      } else {
        setCompanyError(data.error || t('company.save_error'));
      }
    } catch (err) {
      console.error('Error uploading logo:', err);
      setCompanyError(t('company.save_error'));
    } finally {
      setLogoUploading(false);
      e.target.value = ''; // reset input
    }
  };

  const toggleCategory = (catId) => {
    setSelectedCategories(prev =>
      prev.includes(catId) ? prev.filter(id => id !== catId) : [...prev, catId]
    );
    setCompanySuccess('');
  };

  const toggleActivity = (actId) => {
    setSelectedActivities(prev =>
      prev.includes(actId) ? prev.filter(id => id !== actId) : [...prev, actId]
    );
    setCompanySuccess('');
  };

  const toggleRequest = (reqId) => {
    setSelectedRequests(prev =>
      prev.includes(reqId) ? prev.filter(id => id !== reqId) : [...prev, reqId]
    );
    setCompanySuccess('');
  };

  // Filtrer les activités par catégories sélectionnées
  const filteredActivities = selectedCategories.length > 0
    ? activities.filter(a => selectedCategories.includes(a.category_id))
    : activities;

  // Grouper les activités par catégorie
  const activitiesByCategory = filteredActivities.reduce((acc, act) => {
    const catName = act.category_name || 'Other';
    if (!acc[catName]) acc[catName] = [];
    acc[catName].push(act);
    return acc;
  }, {});

  const handleProfileChange = (field, value) => {
    setProfileForm(prev => ({ ...prev, [field]: value }));
    setProfileSuccess('');
  };

  const handleProfileSave = async () => {
    if (!profile?.record_id) return;
    setProfileSaving(true);
    setProfileError('');
    setProfileSuccess('');

    try {
      const res = await fetch(`${API_BASE_URL}/profile/${profile.record_id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(profileForm)
      });
      const data = await res.json();
      if (data.success) {
        setProfileSuccess(t('profile.save_success'));
        updateUser({
          firstname: profileForm.firstname || '',
          lastname:  profileForm.lastname  || '',
        });
        setActiveTab('event');
      } else {
        setProfileError(data.error || t('profile.save_error'));
      }
    } catch (err) {
      console.error('Error saving profile:', err);
      setProfileError(t('profile.save_error'));
    } finally {
      setProfileSaving(false);
    }
  };

  // Redimensionne et compresse une image côté client via canvas (max 800px, JPEG 75%)
  // Résultat typique : 80–200 Ko — évite les 413 et les gros payloads
  const compressImage = (file, maxSize = 800, quality = 0.75) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(objectUrl);
        let { width, height } = img;
        if (width > maxSize || height > maxSize) {
          const ratio = Math.min(maxSize / width, maxSize / height);
          width  = Math.round(width  * ratio);
          height = Math.round(height * ratio);
        }
        const canvas = document.createElement('canvas');
        canvas.width  = width;
        canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        canvas.toBlob(
          blob => blob ? resolve(blob) : reject(new Error('Canvas toBlob failed')),
          'image/jpeg',
          quality
        );
      };
      img.onerror = () => { URL.revokeObjectURL(objectUrl); reject(new Error('Image load failed')); };
      img.src = objectUrl;
    });

  // Lit un Blob/File et retourne son contenu en base64 pur (sans préfixe data:...)
  const blobToBase64 = blob =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload  = () => resolve(reader.result.split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !profile?.record_id) return;

    setPhotoUploading(true);
    try {
      // Comprimer (800px JPEG 75%) puis encoder en base64
      const compressed = await compressImage(file);
      const base64     = await blobToBase64(compressed);

      // Envoi JSON — contourne les limites multipart de Nginx/PHP
      const res = await fetch(`${API_BASE_URL}/profile/${profile.record_id}/photo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ file: base64, filename: 'photo.jpg', contentType: 'image/jpeg' })
      });
      const data = await res.json();
      if (data.success) {
        setProfile(prev => ({ ...prev, profil_picture: data.photo_url }));
      } else {
        setProfileError(data.error || t('profile.save_error'));
      }
    } catch (err) {
      console.error('Error uploading photo:', err);
      setProfileError(t('profile.save_error'));
    } finally {
      setPhotoUploading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!profile?.record_id) return;
    setAccountDeleting(true);
    setAccountDeleteError('');
    try {
      const res = await fetch(`${API_BASE_URL}/account/${profile.record_id}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) {
        setShowAccountModal(false);
        logout();
        const slug = urlSlug || event?.slug;
        window.location.href = slug ? `/event/${slug}/login` : '/login';
      } else {
        setAccountDeleteError(t('account.delete_error'));
      }
    } catch {
      setAccountDeleteError(t('account.delete_error'));
    } finally {
      setAccountDeleting(false);
    }
  };

  const LanguageSelector = () => (
    <div className="flex items-center gap-2">
      <Globe size={20} className="text-gray-600" />
      <select
        value={language}
        onChange={(e) => changeLanguage(e.target.value)}
        className="px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        <option value="fr">🇫🇷 Français</option>
        <option value="de">🇩🇪 Deutsch</option>
        <option value="en">🇬🇧 English</option>
      </select>
    </div>
  );

  const CompanyMatchCard = ({ company, onRequestMeeting, sentRequestRecipientIds = [] }) => {
    const [expanded, setExpanded] = useState(false);
    const [participants, setParticipants] = useState(null);   // null = pas encore chargé
    const [participantsLoading, setParticipantsLoading] = useState(false);
    const matchScore = company.matchScore || 0;

    const handleToggle = async () => {
      const next = !expanded;
      setExpanded(next);
      // Chargement lazy des participants au premier clic
      if (next && participants === null && !participantsLoading) {
        setParticipantsLoading(true);
        try {
          // Passer les user IDs depuis company.users → RECORD_ID()='recXXX' dans Airtable
          const userIds = (company.users || []).join(',');
          const res = await fetch(`${API_BASE_URL}/company-user?ids=${encodeURIComponent(userIds)}`);
          if (res.ok) {
            const data = await res.json();
            setParticipants(data.success ? data.data : []);
          } else {
            setParticipants([]);
          }
        } catch {
          setParticipants([]);
        } finally {
          setParticipantsLoading(false);
        }
      }
    };

    return (
      <div className={`bg-white rounded-xl shadow-sm border overflow-hidden ${matchScore > 0 ? 'border-green-200' : 'border-gray-200'}`}>

        {/* ── En-tête cliquable ── */}
        <button
          className="w-full text-left px-4 py-4 hover:bg-gray-50 transition-colors"
          onClick={handleToggle}
        >
          <div className="flex items-center gap-4">

            {/* Logo */}
            <div className="w-14 h-14 rounded-lg bg-gray-50 border border-gray-200 flex-shrink-0 overflow-hidden flex items-center justify-center">
              {company.logo_thumbnail || company.logo ? (
                <img src={company.logo_thumbnail || company.logo} alt={company.name} className="w-full h-full object-contain p-1" />
              ) : (
                <Building2 size={26} className="text-gray-300" />
              )}
            </div>

            {/* Infos principales */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2 mb-1">
                <h3 className="font-bold text-gray-900 text-base leading-tight">{company.name || '—'}</h3>
                {matchScore > 0 && (
                  <span className="flex-shrink-0 px-2 py-0.5 rounded-full text-xs font-bold bg-green-100 text-green-700 border border-green-200 whitespace-nowrap">
                    {matchScore} {t('matches.score_common')}
                  </span>
                )}
              </div>

              {/* Pays + Type */}
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                {company.country && (
                  <span className="text-xs text-gray-500 flex items-center gap-1">
                    <Globe size={11} /> {company.country}
                  </span>
                )}
                {company.type_name && (
                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100">
                    {company.type_name}
                  </span>
                )}
              </div>

              {/* Tags activités + demandes (aperçu) — matched en surbrillance */}
              <div className="flex flex-wrap gap-1 mt-1.5">
                {(company.activity_names || []).slice(0, 3).map((act, i) => {
                  const isMatched = (company.matchedActivities || []).some(m => m.toLowerCase() === act.toLowerCase());
                  return (
                    <span key={i} className={`px-1.5 py-0.5 rounded text-xs border ${
                      isMatched
                        ? 'bg-green-700 text-white border-green-700 font-semibold'
                        : 'bg-green-50 text-green-700 border-green-100'
                    }`}>{act}</span>
                  );
                })}
                {(company.requests || []).slice(0, 2).map((req, i) => {
                  const isMatched = (company.matchedRequests || []).some(m => m.toLowerCase() === req.name.toLowerCase());
                  return (
                    <span key={i} className={`px-1.5 py-0.5 rounded text-xs border ${
                      isMatched
                        ? 'bg-green-700 text-white border-green-700 font-semibold'
                        : 'bg-orange-50 text-orange-700 border-orange-100'
                    }`}>{req.name}</span>
                  );
                })}
                {((company.activity_names?.length ?? 0) > 3 || (company.requests?.length ?? 0) > 2) && (
                  <span className="text-xs text-gray-400">+{Math.max(0,(company.activity_names?.length ?? 0)-3) + Math.max(0,(company.requests?.length ?? 0)-2)}</span>
                )}
              </div>
            </div>

            {/* Flèche */}
            <ChevronDown
              size={20}
              className={`text-gray-400 flex-shrink-0 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
            />
          </div>
        </button>

        {/* ── Détail (accordéon) ── */}
        {expanded && (
          <div className="border-t border-gray-100 bg-gray-50 px-4 py-4 space-y-4">

            {/* Description */}
            {company.description && (
              <p className="text-sm text-gray-700 leading-relaxed">{company.description}</p>
            )}

            {/* Toutes les activités */}
            {(company.activity_names?.length > 0) && (
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">{t('company.activities') || 'Activités'}</p>
                <div className="flex flex-wrap gap-1">
                  {company.activity_names.map((act, i) => {
                    const isMatched = (company.matchedActivities || []).some(m => m.toLowerCase() === act.toLowerCase());
                    return (
                      <span key={i} className={`px-2 py-0.5 rounded text-xs border ${
                        isMatched
                          ? 'bg-green-700 text-white border-green-700 font-semibold'
                          : 'bg-green-50 text-green-700 border-green-100'
                      }`}>{act}</span>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Toutes les demandes */}
            {(company.requests?.length > 0) && (
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">{t('tabs.my_requests') || 'Demandes'}</p>
                <div className="flex flex-wrap gap-1">
                  {company.requests.map((req, i) => {
                    const isMatched = (company.matchedRequests || []).some(m => m.toLowerCase() === req.name.toLowerCase());
                    return (
                      <span key={i} className={`px-2 py-0.5 rounded text-xs border ${
                        isMatched
                          ? 'bg-orange-500 text-white border-orange-500 font-semibold'
                          : 'bg-orange-50 text-orange-700 border-orange-100'
                      }`}>{req.name}</span>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Contact(s) — chargé au clic, photo + nom + fonction uniquement */}
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">{t('match_card.contact') || 'Contact'}</p>
              {participantsLoading && (
                <div className="flex items-center gap-2 text-sm text-gray-400">
                  <Loader size={14} className="animate-spin" /> Chargement...
                </div>
              )}
              {!participantsLoading && participants !== null && participants.length === 0 && (
                <p className="text-sm text-gray-400 italic">Aucun contact trouvé</p>
              )}
              {!participantsLoading && participants && participants.length > 0 && (
                <div className="flex flex-wrap gap-3">
                  {participants.map(p => (
                    <div key={p.id} className="flex items-start gap-2.5 bg-white rounded-lg px-3 py-2.5 border border-gray-200 shadow-sm">
                      {p.photo ? (
                        <img src={p.photo} alt={`${p.firstname} ${p.lastname}`} className="w-10 h-10 rounded-full object-cover flex-shrink-0 mt-0.5" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-sm font-bold text-blue-600 flex-shrink-0 mt-0.5">
                          {(p.firstname || '?').charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div>
                        <p className="text-sm font-semibold text-gray-900 leading-tight">{p.firstname} {p.lastname}</p>
                        {p.job_title && <p className="text-xs text-gray-500 mt-0.5">{p.job_title}</p>}
                        {p.description && <p className="text-xs text-gray-400 italic mt-1 max-w-xs leading-snug">{p.description}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Raison du match */}
            {company.match?.reason && (
              <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
                <p className="text-xs font-semibold text-blue-600 mb-0.5">{t('match_card.why_match')}</p>
                <p className="text-sm text-blue-900">{company.match.reason}</p>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-2 pt-1">
              {company.linkedin && (
                <a
                  href={company.linkedin.startsWith('http') ? company.linkedin : `https://${company.linkedin}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-3 py-2 rounded-lg border-2 border-blue-200 text-blue-600 hover:bg-blue-50 transition flex items-center gap-1.5 text-sm font-semibold"
                >
                  <Linkedin size={16} />
                </a>
              )}
              {(() => {
                // Déjà une demande envoyée vers un des users de cette société ?
                const alreadySent = (company.users || []).some(uid => sentRequestRecipientIds.includes(uid));
                return alreadySent ? (
                  <div className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-green-50 border border-green-200 text-green-700 text-sm font-semibold">
                    <Check size={16} />
                    {t('request.already_sent') || 'Demande envoyée'}
                  </div>
                ) : (
                  <button
                    onClick={() => onRequestMeeting && onRequestMeeting(company)}
                    className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-blue-700 transition flex items-center justify-center gap-2"
                  >
                    <Send size={16} />
                    {t('request.send_button') || 'Demander un RDV'}
                  </button>
                );
              })()}
            </div>
          </div>
        )}
      </div>
    );
  };

  // eslint-disable-next-line no-unused-vars
  const RequestCard = ({ request }) => (
    <div className={`bg-white rounded-lg shadow-md p-6 border-l-4 ${
      request.type === 'received' ? 'border-green-500' : 'border-orange-500'
    }`}>
      <div className="flex justify-between items-start mb-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className={`px-2 py-1 text-xs rounded-full font-semibold ${
              request.type === 'received' 
                ? 'bg-green-100 text-green-700' 
                : 'bg-orange-100 text-orange-700'
            }`}>
              {request.type === 'received' ? t('request_card.received') : t('request_card.sent')}
            </span>
          </div>
          <h3 className="text-lg font-bold text-gray-900">{request.other_user.name}</h3>
          <p className="text-sm text-gray-600">{request.company}</p>
        </div>
        <span className="text-sm text-gray-500">{request.created_date}</span>
      </div>

      <div className="mb-3">
        <p className="text-sm text-gray-700 mb-2">
          <span className="font-semibold">{t('request_card.message')}</span> {request.message}
        </p>
        <div className="mt-2">
          <p className="text-xs text-gray-600 font-semibold mb-1">{t('request_card.proposed_slots')}</p>
          {request.preferred_slots.map((slot, idx) => (
            <span key={idx} className="inline-block px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs mr-2 mb-1">
              <Clock size={12} className="inline mr-1" />
              {slot.date} {slot.start_time}-{slot.end_time}
            </span>
          ))}
        </div>
      </div>

      {request.type === 'received' && request.status === 'Pending' && (
        <div className="flex gap-2">
          <button 
            onClick={() => handleRespondToRequest(request.id, true, request.preferred_slots[0]?.id)}
            className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition flex items-center justify-center gap-2"
          >
            <Check size={18} />
            {t('request_card.accept')}
          </button>
          <button 
            onClick={() => handleRespondToRequest(request.id, false)}
            className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition flex items-center justify-center gap-2"
          >
            <X size={18} />
            {t('request_card.refuse')}
          </button>
        </div>
      )}
      
      {request.type === 'sent' && (
        <div className="text-sm text-gray-600 italic">{t('request_card.waiting_response')}</div>
      )}
    </div>
  );

  // eslint-disable-next-line no-unused-vars
  const MeetingCard = ({ meeting }) => (
    <div className="bg-white rounded-lg shadow-md p-5 border-l-4 border-purple-500">
      <div className="flex justify-between items-start mb-3">
        <div>
          <h3 className="text-lg font-bold text-gray-900">{meeting.other_user.name}</h3>
          <p className="text-sm text-gray-600">{meeting.company}</p>
        </div>
        <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-semibold">
          {t('meeting_card.confirmed')}
        </span>
      </div>
      
      <div className="grid grid-cols-3 gap-3 text-sm">
        <div className="flex items-center gap-2 text-gray-700">
          <Calendar size={16} className="text-purple-600" />
          <span>{meeting.slot?.date}</span>
        </div>
        <div className="flex items-center gap-2 text-gray-700">
          <Clock size={16} className="text-purple-600" />
          <span>{meeting.slot?.start_time}-{meeting.slot?.end_time}</span>
        </div>
        <div className="flex items-center gap-2 text-gray-700">
          <MapPin size={16} className="text-purple-600" />
          <span>{t('meeting_card.table')} {meeting.table?.id}</span>
        </div>
      </div>
    </div>
  );

  // ── Vue Planning — Étape 1 : gestion des disponibilités ────────────────────
  const PlanningView = () => {
    // Grouper les créneaux par jour
    const slotsByDay = allSlots.reduce((acc, s) => {
      const day = s.day_number || 1;
      if (!acc[day]) acc[day] = [];
      acc[day].push(s);
      return acc;
    }, {});
    const days = Object.keys(slotsByDay).map(Number).sort();
    const currentDay = days.includes(planningDay) ? planningDay : (days[0] || 1);

    // Map slot_id → enregistrement User_Availability
    const availabilityBySlot = {};
    userAvailability.forEach(av => { if (av.slot_id) availabilityBySlot[av.slot_id] = av; });

    const getSlotStatus = (slotId) => {
      const av = availabilityBySlot[slotId];
      if (av?.status === 'Blocked') return 'blocked';
      return 'available'; // pas de record = disponible par défaut
    };

    const currentSlots = slotsByDay[currentDay] || [];

    // Formater "2026-02-10" → "10 févr." / "10 Feb" / "10. Feb"
    const formatDate = (dateStr) => {
      if (!dateStr) return '';
      try {
        return new Date(dateStr + 'T00:00:00').toLocaleDateString(
          language === 'de' ? 'de-DE' : language === 'en' ? 'en-GB' : 'fr-FR',
          { weekday: 'short', day: 'numeric', month: 'short' }
        );
      } catch { return dateStr; }
    };

    if (availabilityLoading) {
      return (
        <div className="flex items-center justify-center py-20">
          <Loader className="animate-spin text-blue-600 mr-3" size={24} />
          <span className="text-gray-500">{t('planning.loading')}</span>
        </div>
      );
    }

    if (allSlots.length === 0) {
      return (
        <div className="bg-white rounded-xl shadow-sm border border-red-100 p-6 text-center">
          <Calendar size={32} className="mx-auto text-gray-300 mb-3" />
          <p className="text-sm font-semibold text-gray-600 mb-1">{t('planning.none_found')}</p>
          <p className="text-xs text-gray-400">
            Sub-event ID : <code className="bg-gray-100 px-1 rounded">{currentSubEventId || '—'}</code>
          </p>
        </div>
      );
    }

    return (
      <div className="space-y-4">

        {/* En-tête */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
          <div className="flex items-center gap-2 mb-1">
            <Calendar size={18} className="text-blue-600" />
            <h2 className="text-base font-bold text-gray-900">{t('planning.title')}</h2>
          </div>
          <p className="text-xs text-gray-400">
            {t('planning.subtitle_chip')} · {allSlots.length} {t('planning.slots_count')}
          </p>
        </div>

        {/* Onglets jours */}
        {days.length > 1 && (
          <div className="flex gap-2 flex-wrap">
            {days.map(day => (
              <button
                key={day}
                onClick={() => setPlanningDay(day)}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${
                  currentDay === day
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'bg-white text-gray-600 border border-gray-200 hover:border-blue-300'
                }`}
              >
                {formatDate(slotsByDay[day]?.[0]?.date) || `${t('planning.day_fallback')} ${day}`}
              </button>
            ))}
          </div>
        )}

        {/* Grille des créneaux */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
          <div className="flex flex-wrap gap-2">
            {currentSlots.map(slot => {
              const status     = getSlotStatus(slot.id);
              const isToggling = !!availabilityToggling[slot.id];

              const styles = {
                available: 'bg-green-100 text-green-700 border-green-400 hover:bg-green-200',
                blocked:   'bg-red-50   text-red-600   border-red-300   hover:bg-red-100',
                unset:     'bg-gray-50  text-gray-500  border-gray-200  hover:bg-gray-100',
              };
              const dots = { available: 'bg-green-500', blocked: 'bg-red-400', unset: 'bg-gray-300' };
              return (
                <button
                  key={slot.id}
                  disabled={isToggling}
                  onClick={() => toggleAvailability(slot, availabilityBySlot)}
                  className={`relative flex flex-col items-center gap-1 px-3 py-2 rounded-lg border-2 text-xs font-semibold transition-all select-none active:scale-95 ${styles[status]} ${isToggling ? 'opacity-50' : ''}`}
                >
                  <span className={`w-2 h-2 rounded-full ${dots[status]}`} />
                  <span className="whitespace-nowrap">{slot.start_time}-{slot.end_time}</span>
                  {status === 'blocked' && (
                    <span className="text-[10px] font-normal opacity-70">
                      {t('planning.chip_blocked')}
                    </span>
                  )}
                  {isToggling && (
                    <span className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-70 rounded-lg">
                      <Loader size={12} className="animate-spin text-gray-500" />
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Légende */}
          <div className="flex gap-4 mt-4 pt-3 border-t border-gray-100">
            {[
              { color: 'bg-green-500', label: t('planning.label_available') },
              { color: 'bg-red-400',   label: t('planning.label_blocked') },
            ].map(({ color, label }) => (
              <div key={label} className="flex items-center gap-1.5 text-xs text-gray-500">
                <span className={`w-2 h-2 rounded-full ${color}`} />
                {label}
              </div>
            ))}
            <button
              onClick={() => {
                setAvailabilityLoading(true);
                Promise.all([
                  fetch(`${API_BASE_URL}/slots-all/${currentSubEventId}`).then(r => r.json()),
                  fetch(`${API_BASE_URL}/availability/${profile?.record_id}?subevent=${currentSubEventId}`).then(r => r.json()),
                ]).then(([s, a]) => {
                  if (s?.success) setAllSlots(s.data || []);
                  if (a?.success) setUserAvailability(a.data || []);
                }).finally(() => setAvailabilityLoading(false));
              }}
              className="ml-auto text-xs text-blue-500 hover:text-blue-700 underline"
            >
              {t('planning.refresh')}
            </button>
          </div>
        </div>
      </div>
    );
  };


  const RequestModal = () => {
    // Grouper les slots par jour
    const slotsByDay = availableSlots.reduce((acc, slot) => {
      const day = slot.day_number || 1;
      if (!acc[day]) acc[day] = [];
      acc[day].push(slot);
      return acc;
    }, {});

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <h2 className="text-2xl font-bold mb-4">{t('request_modal.title')}</h2>
          <p className="text-gray-600 mb-4">
            {t('request_modal.with')} <span className="font-semibold">{selectedMatch?.name}</span> - {selectedMatch?.company}
          </p>
          
          <div className="mb-4">
            <label className="block text-sm font-semibold mb-2">
              {t('request_modal.preferred_slots')} ({selectedSlots.length}/3)
            </label>
            <div className="space-y-4">
              {Object.entries(slotsByDay).map(([day, slots]) => (
                <div key={day}>
                  <h3 className="font-semibold text-sm text-gray-700 mb-2">
                    {t('request_modal.day')} {day}
                  </h3>
                  <div className="grid grid-cols-2 gap-2">
                    {slots.map((slot) => (
                      <label 
                        key={slot.id} 
                        className={`flex items-center gap-2 p-2 rounded cursor-pointer border-2 transition ${
                          selectedSlots.includes(slot.id)
                            ? 'bg-blue-50 border-blue-500'
                            : 'hover:bg-gray-50 border-gray-200'
                        }`}
                      >
                        <input 
                          type="checkbox" 
                          className="w-4 h-4"
                          checked={selectedSlots.includes(slot.id)}
                          onChange={() => toggleSlotSelection(slot.id)}
                        />
                        <span className="text-sm">
                          {slot.start_time} - {slot.end_time}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-semibold mb-2">{t('request_modal.message_label')}</label>
            <textarea 
              id="request-message"
              className="w-full p-3 border border-gray-300 rounded-lg text-sm"
              rows="3"
              placeholder={t('request_modal.message_placeholder')}
            />
          </div>

          <div className="flex gap-2">
            <button 
              onClick={() => {
                setShowRequestModal(false);
                setSelectedSlots([]);
              }}
              className="flex-1 px-4 py-2 border-2 border-gray-300 rounded-lg hover:bg-gray-50 transition"
            >
              {t('request_modal.cancel')}
            </button>
            <button 
              onClick={handleSendRequest}
              className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition font-semibold"
            >
              {t('request_modal.send')}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Erreur réelle (pas d'événement après hydratation, ou erreur API)
  if (error && !loading) {
    const isNoEvent = error === 'no_event' || !currentSubEventId;
    const loginPath = event?.slug ? `/event/${event.slug}/login` : '/login';
    const noEventMsg = { fr: 'Aucun événement sélectionné', de: 'Keine Veranstaltung ausgewählt', en: 'No event selected' };
    const noUserMsg  = { fr: 'Aucun utilisateur connecté', de: 'Kein Benutzer angemeldet', en: 'No user connected' };
    const lang = localStorage.getItem('language') || 'fr';
    const errorTitle = isNoEvent
      ? (noEventMsg[lang] || noEventMsg.fr)
      : (!currentUserId ? (noUserMsg[lang] || noUserMsg.fr) : error);
    const loginLabel = { fr: 'S\'identifier', de: 'Anmelden', en: 'Log in' };

    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="text-red-500 text-6xl mb-4">⚠</div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">{errorTitle}</h2>
          <button
            onClick={() => window.location.href = loginPath}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition"
          >
            {loginLabel[lang] || loginLabel.fr}
          </button>
        </div>
      </div>
    );
  }

  // Loading : skeleton progressif — phase 1 (profil absent) ou phase 2 (profil prêt, données en cours)
  if (loading) {
    const userName    = user?.firstname ? `${user.firstname} ${user.lastname || ''}`.trim() : '';
    const userInitial = (user?.firstname || '?').charAt(0).toUpperCase();
    return (
      <DashboardSkeleton
        profileReady = {!!profile?.record_id}
        userName     = {userName}
        userInitial  = {userInitial}
        userPhoto    = {profile?.profil_picture || null}
        eventName    = {event?.name || ''}
        subEventName = {subEvent?.name || ''}
        onReload     = {handleManualRefresh}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 sm:py-4">
          <div className="flex justify-between items-center gap-2">

            {/* Gauche : logo + titre événement */}
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              {event?.logo_dark && (
                <img
                  src={event.logo_dark}
                  alt={event?.name}
                  className="h-7 sm:h-11 object-contain flex-shrink-0"
                  onError={() => { const slug = urlSlug || event?.slug; if (slug) loadEventBySlug(slug); }}
                />
              )}
              <div className="min-w-0">
                <p className="text-xs sm:text-sm text-gray-700 font-semibold truncate">{subEvent?.name || event?.name || ''}</p>
              </div>
            </div>

            {/* Droite : langue + nom + avatar (→ profil) + déco */}
            <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
              {/* Sélecteur de langue — masqué sur mobile */}
              <div className="hidden md:block">
                <LanguageSelector />
              </div>
              {/* Bouton reload */}
              <button
                onClick={handleManualRefresh}
                title={t('app.reload') || 'Recharger les données'}
                className="p-1.5 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition"
              >
                <RefreshCw size={15} />
              </button>
              {/* Nom utilisateur — masqué sur petits écrans */}
              <div className="hidden sm:block text-right">
                <p className="text-xs text-gray-500">{t('app.connected_as')}</p>
                <p className="text-sm font-semibold text-gray-900 leading-tight">
                  {user?.firstname ? `${user.firstname} ${user.lastname || ''}`.trim() : 'Utilisateur'}
                </p>
              </div>
              {/* Avatar cliquable → onglet Profil + ring au survol */}
              <button
                onClick={() => setActiveTab('profile')}
                title={t('tabs.profile')}
                className={`flex-shrink-0 rounded-full transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                  activeTab === 'profile'
                    ? 'ring-2 ring-blue-600 ring-offset-2'
                    : 'hover:ring-2 hover:ring-blue-400 hover:ring-offset-1'
                }`}
              >
                {profile?.profil_picture ? (
                  <img
                    src={profile.profil_picture}
                    alt="Profile"
                    className="w-9 h-9 sm:w-11 sm:h-11 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-9 h-9 sm:w-11 sm:h-11 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-sm sm:text-lg">
                    {(user?.firstname || '?').charAt(0).toUpperCase()}
                  </div>
                )}
              </button>
              {/* Bouton déconnexion */}
              <button
                onClick={handleLogout}
                className="flex items-center gap-1.5 p-2 sm:px-3 sm:py-2 text-sm text-red-500 hover:bg-red-50 rounded-lg transition"
                title={t('app.logout')}
              >
                <LogOut size={18} />
                <span className="hidden sm:inline">{t('app.logout')}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs — scrollable sur mobile, profil accessible via l'avatar */}
      <div className="bg-white shadow-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-2 sm:px-4">
          <div className="flex overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
            {[
              { key: 'event',      icon: <Home size={16} />,          label: t('tabs.event'),                  badge: null, hidden: contentDisplay.find(c => c.type_id === 'Articles')?.status === 'off' },
              { key: 'matches',    icon: <Users size={16} />,         label: t('tabs.matches'),                badge: companiesMatches.length, hidden: contentDisplay.find(c => c.type_id === 'Matches')?.status === 'off' },
              { key: 'requests',   icon: <MessageSquare size={16} />, label: t('tabs.requests'),               badge: meetingRequests.filter(r => r.recipient_id === profile?.record_id && r.status === 'Pending').length || null, hidden: contentDisplay.find(c => c.type_id === 'Request')?.status === 'off' },
              { key: 'meetings',   icon: <Calendar size={16} />,      label: t('tabs.meetings'),               badge: meetingRequests.filter(r => r.status === 'Confirmed').length || null, hidden: contentDisplay.find(c => c.type_id === 'Planning')?.status === 'off' },
              { key: 'networking', icon: <HeartHandshake size={16} />, label: t('tabs.networking') || 'Network', badge: null, hidden: contentDisplay.find(c => c.type_id === 'Networking')?.status === 'off' },
              { key: 'company',    icon: <Building2 size={16} />,     label: t('tabs.company'),                badge: null },
              { key: 'profile',    icon: <User size={16} />,          label: t('tabs.profile') || 'Profil',    badge: null },
              { key: 'badge',      icon: <Ticket size={16} />,        label: t('tabs.badge') || 'Badge',       badge: null, hidden: contentDisplay.find(c => c.type_id === 'Badge')?.status === 'off' },
            ].filter(tab => !tab.hidden).map(({ key, icon, label, badge }) => (
              <button
                key={key}
                onClick={() => { setActiveTab(key); if (key === 'requests' || key === 'meetings') loadMeetingRequests(); if (key === 'event') loadArticles(); }}
                className={`flex-shrink-0 flex items-center gap-1.5 px-3 sm:px-5 py-3 sm:py-4 font-semibold text-xs sm:text-sm transition-all whitespace-nowrap border-b-4 ${
                  activeTab === key
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-900 hover:border-gray-200'
                }`}
              >
                {icon}
                <span>{label}</span>
                {badge !== null && (
                  <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${
                    activeTab === key ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'
                  }`}>{badge}</span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
        {/* ===== ONGLET YOUR EVENT ===== */}
        {activeTab === 'event' && (
          <div className="max-w-2xl mx-auto px-4 py-6">
            {articlesLoading ? (
              <div className="flex items-center justify-center py-16">
                <Loader className="animate-spin text-blue-600" size={28} />
              </div>
            ) : articles.length === 0 ? (
              <div className="text-center py-16 text-gray-400">
                <Home size={40} className="mx-auto mb-3 opacity-30" />
                <p className="text-sm">No content available yet.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {articles.map((article, idx) => {
                  const isExpanded = !!expandedArticles[article.id];
                  const isFirst = idx === 0;
                  return (
                    <div key={article.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                      {/* Banner image */}
                      {article.banner_url && (
                        <div className="relative">
                          <img
                            src={article.banner_url}
                            alt={article.title}
                            className="w-full object-cover"
                            style={{ maxHeight: isFirst ? '220px' : '160px' }}
                          />
                        </div>
                      )}

                      {/* Header cliquable */}
                      <button
                        className="w-full text-left px-5 py-4 flex items-start justify-between gap-3 hover:bg-gray-50 transition"
                        onClick={() => setExpandedArticles(prev => ({ ...prev, [article.id]: !prev[article.id] }))}
                      >
                        <div className="flex-1 min-w-0">
                          <h2 className="font-bold text-gray-900 text-base leading-snug">{article.title}</h2>
                          {article.subtitle && (
                            <p className="text-sm text-blue-600 font-medium mt-0.5">{article.subtitle}</p>
                          )}
                        </div>
                        <div className="flex-shrink-0 mt-0.5">
                          {isExpanded
                            ? <ChevronUp size={18} className="text-gray-400" />
                            : <ChevronDown size={18} className="text-gray-400" />
                          }
                        </div>
                      </button>

                      {/* Contenu expandable */}
                      {isExpanded && (
                        <div className="px-5 pb-5">
                          {/* Vidéo YouTube */}
                          {article.video_url && (
                            <div className="mb-4 rounded-xl overflow-hidden bg-black aspect-video">
                              <iframe
                                src={article.video_url.replace('watch?v=', 'embed/')}
                                title={article.title}
                                className="w-full h-full"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                              />
                            </div>
                          )}
                          {/* Description HTML */}
                          {article.description && (
                            <div
                              className="prose prose-sm max-w-none text-gray-700 text-sm leading-relaxed"
                              dangerouslySetInnerHTML={{ __html: article.description }}
                            />
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {activeTab === 'matches' && (() => {
          // ── Content-display : état de la feature Matches ──
          const matchesConfig = contentDisplay.find(c => c.type_id === 'Matches');
          const matchesStatus = matchesConfig?.status ?? 'on'; // défaut : on

          // Verrouillé si : status = 'off' OU status = 'on' avec date future
          const isLocked = (() => {
            if (matchesStatus === 'off') return true;
            const pubDate = matchesConfig?.publication_date;
            if (!pubDate) return false;
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const pub = new Date(pubDate);
            pub.setHours(0, 0, 0, 0);
            return pub > today;
          })();

          return (
          <div>
            {/* ── Bannière de verrouillage ── */}
            {isLocked && (
              <div className="relative mb-6 rounded-2xl overflow-hidden border border-blue-100 shadow-md bg-white">
                {/* Fond flou décoratif */}
                <div className="absolute inset-0 opacity-5"
                  style={{ backgroundImage: 'repeating-linear-gradient(45deg, #2563eb 0, #2563eb 1px, transparent 0, transparent 50%)', backgroundSize: '12px 12px' }} />
                <div className="relative z-10 flex flex-col items-center justify-center text-center px-6 py-10 gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-blue-50 flex items-center justify-center shadow-sm">
                    <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                      <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                    </svg>
                  </div>
                  <div>
                    <p className="text-lg font-bold text-gray-900 mb-1">
                      {matchesConfig?.message_title || 'Coming soon'}
                    </p>
                    <p className="text-sm text-gray-500 max-w-md mx-auto">
                      {matchesConfig?.message || 'Matches will be available soon.'}
                    </p>
                    {matchesConfig?.publication_date && (
                      <p className="mt-2 inline-block bg-blue-50 text-blue-700 text-xs font-semibold px-3 py-1 rounded-full">
                        {new Date(matchesConfig.publication_date).toLocaleDateString(language === 'fr' ? 'fr-FR' : language === 'de' ? 'de-DE' : 'en-US', { day: 'numeric', month: 'long', year: 'numeric' })}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Contenu verrouillé : cards floutées en arrière-plan */}
            {isLocked ? (
              <div className="relative">
                <div className="pointer-events-none select-none" style={{ filter: 'blur(4px)', opacity: 0.35 }}>
                  {/* Affichage fantôme des filtres */}
                  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 mb-4 h-20" />
                  <div className="flex flex-col gap-3">
                    {[1,2,3].map(i => (
                      <div key={i} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 h-20" />
                    ))}
                  </div>
                </div>
              </div>
            ) : (
            <div>
            {/* Barre de recherche, tri et filtres par tags */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 sm:p-4 mb-4">

              {/* Ligne 1 : recherche (pleine largeur) */}
              <div className="relative mb-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={15} />
                <input
                  type="text"
                  placeholder={t('filters.search_placeholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Ligne 2 : boutons de tri — wrap sur mobile */}
              <div className="flex flex-wrap gap-1.5 mb-2">
                {[
                  { key: 'score',   label: t('filters.sort_score')   },
                  { key: 'az',      label: t('filters.sort_az')      },
                  { key: 'country', label: t('filters.sort_country') },
                  { key: 'type',    label: t('filters.sort_type')    },
                ].map(({ key, label }) => (
                  <button
                    key={key}
                    onClick={() => setSortBy(key)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all ${
                      sortBy === key
                        ? 'bg-blue-600 text-white shadow-sm'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {/* Ligne 3 : tags cliquables (activités + demandes du user = ses intérêts) */}
              {userTags.length > 0 && (
                <div className="flex flex-wrap items-center gap-1.5 pt-1 border-t border-gray-100">
                  <span className="text-[11px] text-gray-400 font-semibold uppercase tracking-wide self-center">
                    {t('filters.filter_label')}
                  </span>
                  {userTags.map(tag => {
                    const isActive = activeTagFilters.includes(tag.name);
                    return (
                      <button
                        key={tag.name}
                        onClick={() =>
                          setActiveTagFilters(prev =>
                            isActive ? prev.filter(t => t !== tag.name) : [...prev, tag.name]
                          )
                        }
                        className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-all ${
                          tag.type === 'activity'
                            ? isActive
                              ? 'bg-green-600 text-white border-green-600'
                              : 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100'
                            : isActive
                              ? 'bg-orange-500 text-white border-orange-500'
                              : 'bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-100'
                        }`}
                      >
                        {isActive && '✓ '}{tag.name}
                      </button>
                    );
                  })}
                  {activeTagFilters.length > 0 && (
                    <button
                      onClick={() => setActiveTagFilters([])}
                      className="ml-1 text-[11px] text-gray-400 hover:text-gray-600 underline self-center"
                    >
                      {t('filters.clear_all')}
                    </button>
                  )}
                </div>
              )}
            </div>

            <div className="flex flex-col gap-3">
              {filteredAndSorted.length > 0 ? (
                filteredAndSorted.map(company => (
                  <CompanyMatchCard
                    key={company.company_id}
                    company={company}
                    onRequestMeeting={openRdvModal}
                    sentRequestRecipientIds={meetingRequests
                      .filter(r => r.requester_id === profile?.record_id && ['Pending','Accepted','Confirmed'].includes(r.status))
                      .map(r => r.recipient_id)}
                  />
                ))
              ) : (
                <div className="text-center py-12 text-gray-500">
                  {t('matches.no_results') || 'Aucun match trouvé'}
                </div>
              )}
            </div>
            </div>
            )}
          </div>
          );
        })()}

        {activeTab === 'requests' && (() => {
          const requestConfig = contentDisplay.find(c => c.type_id === 'Request');
          const requestStatus = requestConfig?.status ?? 'on';

          const isRequestLocked = (() => {
            if (requestStatus === 'off') return true;
            const pubDate = requestConfig?.publication_date;
            if (!pubDate) return false;
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const pub = new Date(pubDate);
            pub.setHours(0, 0, 0, 0);
            return pub > today;
          })();

          if (isRequestLocked) return (
            <div className="relative rounded-2xl overflow-hidden border border-blue-100 shadow-md bg-white">
              <div className="absolute inset-0 opacity-5"
                style={{ backgroundImage: 'repeating-linear-gradient(45deg, #2563eb 0, #2563eb 1px, transparent 0, transparent 50%)', backgroundSize: '12px 12px' }} />
              <div className="relative z-10 flex flex-col items-center justify-center text-center px-6 py-10 gap-4">
                <div className="w-14 h-14 rounded-2xl bg-blue-50 flex items-center justify-center shadow-sm">
                  <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                  </svg>
                </div>
                <div>
                  <p className="text-lg font-bold text-gray-900 mb-1">
                    {requestConfig?.message_title || 'Coming soon'}
                  </p>
                  <p className="text-sm text-gray-500 max-w-md mx-auto">
                    {requestConfig?.message || 'Meeting requests will be available soon.'}
                  </p>
                  {requestConfig?.publication_date && (
                    <p className="mt-2 inline-block bg-blue-50 text-blue-700 text-xs font-semibold px-3 py-1 rounded-full">
                      {new Date(requestConfig.publication_date).toLocaleDateString(language === 'fr' ? 'fr-FR' : language === 'de' ? 'de-DE' : 'en-US', { day: 'numeric', month: 'long', year: 'numeric' })}
                    </p>
                  )}
                </div>
              </div>
            </div>
          );

          // Filtrage par statut + recherche
          const filterReqs = (list) => {
            let f = list;
            if (requestStatusFilter.length > 0)
              f = f.filter(r => requestStatusFilter.includes(r.status));
            if (requestSearchTerm.trim()) {
              const s = requestSearchTerm.toLowerCase();
              f = f.filter(r => {
                const isSent  = r.requester_id === profile?.record_id;
                const name    = (isSent ? r.recipient_name    : r.requester_name)    || '';
                const company = (isSent ? r.recipient_company : r.requester_company) || '';
                return name.toLowerCase().includes(s) || company.toLowerCase().includes(s);
              });
            }
            return f;
          };
          const received = filterReqs(meetingRequests.filter(r => r.recipient_id === profile?.record_id));
          const sent     = filterReqs(meetingRequests.filter(r => r.requester_id === profile?.record_id));

          const statusBadge = (status) => {
            // Couleurs identiques aux tags de filtre
            const map = {
              Pending:   'bg-yellow-100 text-yellow-700 border-yellow-200',
              Accepted:  'bg-green-100  text-green-700  border-green-200',
              Confirmed: 'bg-green-700  text-white       border-green-700',
              Rejected:  'bg-red-100    text-red-600    border-red-200',
              Cancelled: 'bg-red-100    text-red-600    border-red-200',
              Expired:   'bg-gray-100   text-gray-400   border-gray-200',
            };
            const labels = {
              Pending:   t('request.status_pending')   || 'En attente',
              Accepted:  t('request.status_accepted')  || 'Acceptée',
              Confirmed: t('request.status_confirmed') || 'Confirmée',
              Rejected:  t('request.status_rejected')  || 'Refusée',
              Cancelled: t('request.status_cancelled') || 'Annulée',
              Expired:   t('request.status_expired')   || 'Expirée',
            };
            return <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${map[status] || map.Expired}`}>{labels[status] || status}</span>;
          };

          const RequestRow = ({ req, isSent }) => {
            const photo    = isSent ? req.recipient_photo    : req.requester_photo;
            const name     = isSent ? req.recipient_name     : req.requester_name;
            const fonction = isSent ? req.recipient_fonction  : req.requester_fonction;
            const company  = isSent ? req.recipient_company  : req.requester_company;
            const logo     = isSent ? req.recipient_logo     : req.requester_logo;
            const dt       = req.created_date ? new Date(req.created_date) : null;
            const dateStr  = dt ? dt.toLocaleDateString(language, { day: 'numeric', month: 'short', year: 'numeric' }) : '';
            const timeStr  = dt ? dt.toLocaleTimeString(language, { hour: '2-digit', minute: '2-digit' }) : '';

            return (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 space-y-3">
                {/* Ligne société : logo + nom société */}
                <div className="flex items-center gap-2.5">
                  {logo
                    ? <img src={logo} alt={company} className="w-8 h-8 rounded-lg object-contain border border-gray-100 bg-gray-50 p-0.5 flex-shrink-0" />
                    : <div className="w-8 h-8 rounded-lg bg-gray-50 border border-gray-200 flex items-center justify-center flex-shrink-0"><Building2 size={14} className="text-gray-300" /></div>
                  }
                  <p className="text-xs font-bold text-gray-700 flex-1 truncate">{company || '—'}</p>
                  <div className="flex flex-col items-end gap-0.5 flex-shrink-0">
                    {statusBadge(req.status)}
                    <span className="text-[10px] text-gray-400">{dateStr} · {timeStr}</span>
                  </div>
                </div>

                {/* Ligne personne : photo + nom + fonction */}
                <div className="flex items-center gap-3">
                  {photo
                    ? <img src={photo} alt={name} className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
                    : <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0"><User size={18} className="text-gray-400" /></div>
                  }
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-gray-900 truncate">{name || t('request.unknown_person')}</p>
                    {fonction && <p className="text-xs text-gray-400 truncate">{fonction}</p>}
                  </div>
                </div>

                {/* Message */}
                {req.message && (
                  <p className="text-sm text-gray-600 bg-gray-50 rounded-lg px-3 py-2 italic">"{req.message}"</p>
                )}

                {/* Actions — demandes reçues en attente */}
                {!isSent && req.status === 'Pending' && (
                  <div className="flex gap-2 pt-1">
                    <button
                      onClick={() => updateMeetingRequestStatus(req.id, 'Accepted')}
                      className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-green-700 transition flex items-center justify-center gap-1.5"
                    >
                      <Check size={15} />{t('request.accept')}
                    </button>
                    <button
                      onClick={() => updateMeetingRequestStatus(req.id, 'Rejected')}
                      className="flex-1 border border-red-300 text-red-500 px-4 py-2 rounded-lg text-sm font-semibold hover:bg-red-50 transition flex items-center justify-center gap-1.5"
                    >
                      <X size={15} />{t('request.reject')}
                    </button>
                  </div>
                )}

                {/* Action — annuler une demande envoyée en attente */}
                {isSent && req.status === 'Pending' && (
                  <button
                    onClick={() => updateMeetingRequestStatus(req.id, 'Cancelled')}
                    className="w-full border border-gray-200 text-gray-500 px-4 py-2 rounded-lg text-sm font-semibold hover:bg-gray-50 transition flex items-center justify-center gap-1.5"
                  >
                    <X size={15} />{t('request.cancel') || 'Annuler la demande'}
                  </button>
                )}

                {/* Message de réponse */}
                {req.response_message && (
                  <p className="text-xs text-gray-500 italic border-t border-gray-100 pt-2">"{req.response_message}"</p>
                )}
              </div>
            );
          };

          return (
            <div className="space-y-4">

              {/* Barre filtres + recherche */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 space-y-2.5">
                {/* Recherche */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={15} />
                  <input
                    type="text"
                    placeholder={t('request.search_placeholder')}
                    value={requestSearchTerm}
                    onChange={e => setRequestSearchTerm(e.target.value)}
                    className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                {/* Tags statut */}
                <div className="flex gap-1.5 flex-wrap">
                  {[
                    { status: '',          label: t('request.filter_all'),       cls: 'bg-gray-100 text-gray-600 border-gray-200' },
                    { status: 'Confirmed', label: t('request.status_confirmed'), cls: 'bg-green-700 text-white border-green-700' },
                    { status: 'Accepted',  label: t('request.status_accepted'),  cls: 'bg-green-100 text-green-700 border-green-200' },
                    { status: 'Pending',   label: t('request.status_pending'),   cls: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
                    { status: 'Cancelled', label: t('request.status_cancelled'), cls: 'bg-red-100 text-red-600 border-red-200' },
                  ].map(({ status, label, cls }) => {
                    const active = status === '' ? requestStatusFilter.length === 0 : requestStatusFilter.includes(status);
                    return (
                      <button
                        key={status}
                        onClick={() => {
                          if (status === '') { setRequestStatusFilter([]); }
                          else { setRequestStatusFilter(prev => prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]); }
                        }}
                        className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-all ${cls} ${active ? 'opacity-100 ring-2 ring-offset-1 ring-blue-400' : 'opacity-55 hover:opacity-90'}`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Loader rafraîchissement */}
              {requestsLoading && (
                <div className="flex items-center justify-center py-4 text-gray-400 gap-2">
                  <Loader className="animate-spin" size={16} />
                  <span className="text-xs">{t('planning.loading')}</span>
                </div>
              )}

              {/* Demandes reçues */}
              {!requestsLoading && received.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2">
                    <MessageSquare size={12} />{t('request.section_received') || 'Demandes reçues'}
                    {received.filter(r => r.status === 'Pending').length > 0 && (
                      <span className="bg-yellow-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                        {received.filter(r => r.status === 'Pending').length}
                      </span>
                    )}
                  </h3>
                  {received.map(req => <RequestRow key={req.id} req={req} isSent={false} />)}
                </div>
              )}

              {/* Demandes envoyées */}
              {!requestsLoading && sent.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2">
                    <Send size={12} />{t('request.section_sent') || 'Demandes envoyées'}
                  </h3>
                  {sent.map(req => <RequestRow key={req.id} req={req} isSent={true} />)}
                </div>
              )}

              {/* Vide */}
              {!requestsLoading && received.length === 0 && sent.length === 0 && (
                <div className="text-center py-16 text-gray-400">
                  <MessageSquare size={36} className="mx-auto mb-3 text-gray-200" />
                  <p className="text-sm">{t('request.no_results') || 'Aucune demande de RDV'}</p>
                </div>
              )}
            </div>
          );
        })()}

        {activeTab === 'meetings' && (() => {
          const planningConfig = contentDisplay.find(c => c.type_id === 'Planning');
          const planningStatus = planningConfig?.status ?? 'on';

          const isPlanningLocked = (() => {
            if (planningStatus === 'off') return true;
            const pubDate = planningConfig?.publication_date;
            if (!pubDate) return false;
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const pub = new Date(pubDate);
            pub.setHours(0, 0, 0, 0);
            return pub > today;
          })();

          if (isPlanningLocked) return (
            <div className="relative rounded-2xl overflow-hidden border border-blue-100 shadow-md bg-white">
              <div className="absolute inset-0 opacity-5"
                style={{ backgroundImage: 'repeating-linear-gradient(45deg, #2563eb 0, #2563eb 1px, transparent 0, transparent 50%)', backgroundSize: '12px 12px' }} />
              <div className="relative z-10 flex flex-col items-center justify-center text-center px-6 py-10 gap-4">
                <div className="w-14 h-14 rounded-2xl bg-blue-50 flex items-center justify-center shadow-sm">
                  <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                  </svg>
                </div>
                <div>
                  <p className="text-lg font-bold text-gray-900 mb-1">
                    {planningConfig?.message_title || 'Coming soon'}
                  </p>
                  <p className="text-sm text-gray-500 max-w-md mx-auto">
                    {planningConfig?.message || 'Your planning will be available soon.'}
                  </p>
                  {planningConfig?.publication_date && (
                    <p className="mt-2 inline-block bg-blue-50 text-blue-700 text-xs font-semibold px-3 py-1 rounded-full">
                      {new Date(planningConfig.publication_date).toLocaleDateString(language === 'fr' ? 'fr-FR' : language === 'de' ? 'de-DE' : 'en-US', { day: 'numeric', month: 'long', year: 'numeric' })}
                    </p>
                  )}
                </div>
              </div>
            </div>
          );

          return (
          <div className="space-y-4">
            {/* Sous-onglets */}
            <div className="flex bg-white rounded-xl shadow-sm border border-gray-100 p-1 gap-1">
              {[
                { key: 'meetings',     label: t('planning.tab_meetings'),     icon: <Clock size={14} />    },
                { key: 'availability', label: t('planning.tab_availability'), icon: <Calendar size={14} /> },
              ].map(({ key, label, icon }) => (
                <button
                  key={key}
                  onClick={() => setPlanningSubTab(key)}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg text-sm font-semibold transition-all ${
                    planningSubTab === key
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-gray-500 hover:text-gray-800 hover:bg-gray-50'
                  }`}
                >
                  {icon}{label}
                </button>
              ))}
            </div>

            {/* Contenu sous-onglet */}
            {planningSubTab === 'availability' && <PlanningView />}

            {planningSubTab === 'meetings' && (() => {
              const myMeetingsList = meetingRequests.filter(r =>
                ['Confirmed', 'Accepted', 'Cancelled', 'Pending'].includes(r.status)
              );

              const uniqueDays = [...new Set(
                myMeetingsList.map(r => r.confirmed_slot_day).filter(Boolean)
              )].sort((a, b) => a - b);

              const statusOptions = [
                { status: null,        label: t('request.filter_all'),       cls: 'bg-gray-100 text-gray-600 border-gray-200' },
                { status: 'Confirmed', label: t('request.status_confirmed'), cls: 'bg-green-100 text-green-700 border-green-200' },
                { status: 'Accepted',  label: t('request.status_accepted'),  cls: 'bg-blue-100 text-blue-700 border-blue-200' },
                { status: 'Pending',   label: t('request.status_pending'),   cls: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
                { status: 'Cancelled', label: t('request.status_cancelled'), cls: 'bg-red-100 text-red-600 border-red-200' },
              ].filter(opt => opt.status === null || myMeetingsList.some(r => r.status === opt.status));

              const statusPriority = { Confirmed: 0, Accepted: 1, Pending: 2, Cancelled: 99 };

              const filtered = myMeetingsList.filter(r => {
                if (meetingsStatusFilter && r.status !== meetingsStatusFilter) return false;
                const dayOk = meetingsDayFilter === null || r.confirmed_slot_day === meetingsDayFilter;
                if (!dayOk) return false;
                if (meetingsSearchTerm.trim()) {
                  const isSent  = r.requester_id === profile?.record_id;
                  const s       = meetingsSearchTerm.toLowerCase();
                  const name    = (isSent ? r.recipient_name    : r.requester_name)    || '';
                  const company = (isSent ? r.recipient_company : r.requester_company) || '';
                  return name.toLowerCase().includes(s) || company.toLowerCase().includes(s);
                }
                return true;
              }).sort((a, b) => {
                const pa = statusPriority[a.status] ?? 50;
                const pb = statusPriority[b.status] ?? 50;
                if (pa !== pb) return pa - pb;
                if ((a.confirmed_slot_day || 0) !== (b.confirmed_slot_day || 0))
                  return (a.confirmed_slot_day || 0) - (b.confirmed_slot_day || 0);
                return (a.confirmed_slot_start || '').localeCompare(b.confirmed_slot_start || '');
              });

              const statusBadge = (status) => {
                const cfg = {
                  Confirmed: { cls: 'bg-green-100 text-green-700 border-green-200',    label: t('request.status_confirmed') },
                  Accepted:  { cls: 'bg-blue-100 text-blue-700 border-blue-200',       label: t('request.status_accepted') },
                  Pending:   { cls: 'bg-yellow-100 text-yellow-700 border-yellow-200', label: t('request.status_pending') },
                  Cancelled: { cls: 'bg-red-100 text-red-600 border-red-200',          label: t('request.status_cancelled') },
                };
                const c = cfg[status] || cfg.Pending;
                return <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${c.cls}`}>{c.label}</span>;
              };

              return (
                <div className="space-y-4">

                  {/* Modale annulation */}
                  {cancelModal.open && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
                      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 space-y-4">
                        <h3 className="text-base font-bold text-gray-900">{t('planning.cancel_title')}</h3>
                        <p className="text-sm text-gray-500">{t('planning.cancel_desc')}</p>
                        <textarea
                          rows={3}
                          placeholder={t('planning.cancel_message_placeholder')}
                          value={cancelModal.message}
                          onChange={e => setCancelModal(prev => ({ ...prev, message: e.target.value }))}
                          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400 resize-none"
                        />
                        <div className="flex gap-2 pt-1">
                          <button
                            onClick={() => setCancelModal(cancelModalDefault)}
                            className="flex-1 border border-gray-200 text-gray-600 py-2.5 rounded-lg text-sm font-semibold hover:bg-gray-50 transition"
                          >
                            {t('planning.cancel_back')}
                          </button>
                          <button
                            disabled={cancelModal.loading}
                            onClick={async () => {
                              setCancelModal(prev => ({ ...prev, loading: true }));
                              await updateMeetingRequestStatus(cancelModal.requestId, 'Cancelled', cancelModal.message);
                              setCancelModal(cancelModalDefault);
                            }}
                            className="flex-1 bg-red-600 text-white py-2.5 rounded-lg text-sm font-semibold hover:bg-red-700 transition disabled:opacity-60"
                          >
                            {cancelModal.loading ? '...' : t('planning.cancel_confirm')}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Barre filtres */}
                  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 space-y-2.5">

                    {/* Recherche + bouton agenda */}
                    <div className="flex gap-2 items-center">
                      <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={15} />
                        <input
                          type="text"
                          placeholder={t('planning.search_meetings')}
                          value={meetingsSearchTerm}
                          onChange={e => setMeetingsSearchTerm(e.target.value)}
                          className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      <button
                        onClick={handleSendMyAgenda}
                        disabled={sendAgendaState === 'sending' || !profile?.email}
                        className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold transition whitespace-nowrap disabled:opacity-60 ${
                          sendAgendaState === 'sent'    ? 'bg-green-100 text-green-700' :
                          sendAgendaState === 'error'   ? 'bg-red-100 text-red-700' :
                          sendAgendaState === 'sending' ? 'bg-blue-50 text-blue-400' :
                          'bg-blue-600 text-white hover:bg-blue-700'
                        }`}
                      >
                        {sendAgendaState === 'sending' ? <Loader size={14} className="animate-spin" /> :
                         sendAgendaState === 'sent'    ? <Check size={14} /> :
                         <Mail size={14} />}
                        {sendAgendaState === 'sent'    ? t('planning.send_agenda_ok') :
                         sendAgendaState === 'error'   ? t('planning.send_agenda_error') :
                         sendAgendaState === 'sending' ? t('planning.send_agenda_sending') :
                         t('planning.send_agenda_btn')}
                      </button>
                    </div>

                    {/* Boutons jours (si > 2 jours) */}
                    {uniqueDays.length > 2 && (
                      <div className="flex gap-1.5 flex-wrap">
                        <button
                          onClick={() => setMeetingsDayFilter(null)}
                          className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-all bg-gray-100 text-gray-600 border-gray-200 ${meetingsDayFilter === null ? 'opacity-100 ring-2 ring-offset-1 ring-blue-400' : 'opacity-55 hover:opacity-90'}`}
                        >
                          {t('planning.all_days')}
                        </button>
                        {uniqueDays.map(day => {
                          const sample  = myMeetingsList.find(r => r.confirmed_slot_day === day);
                          const dateStr = sample?.confirmed_slot_date
                            ? new Date(sample.confirmed_slot_date).toLocaleDateString(language, { day: 'numeric', month: 'short' })
                            : '';
                          return (
                            <button
                              key={day}
                              onClick={() => setMeetingsDayFilter(meetingsDayFilter === day ? null : day)}
                              className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-all bg-blue-100 text-blue-700 border-blue-200 ${meetingsDayFilter === day ? 'opacity-100 ring-2 ring-offset-1 ring-blue-400' : 'opacity-55 hover:opacity-90'}`}
                            >
                              {t('planning.day_fallback')} {day}{dateStr ? ` · ${dateStr}` : ''}
                            </button>
                          );
                        })}
                      </div>
                    )}

                    {/* Filtres statut */}
                    {statusOptions.length > 1 && (
                      <div className="flex gap-1.5 flex-wrap">
                        {statusOptions.map(({ status, label, cls }) => {
                          const isActive = meetingsStatusFilter === status;
                          return (
                            <button
                              key={status ?? 'all'}
                              onClick={() => setMeetingsStatusFilter(isActive && status !== null ? null : status)}
                              className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-all ${cls} ${isActive ? 'opacity-100 ring-2 ring-offset-1 ring-blue-400' : 'opacity-55 hover:opacity-90'}`}
                            >
                              {label}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Cards RDV */}
                  {filtered.length > 0 ? filtered.map(r => {
                    const isCancelled = r.status === 'Cancelled';
                    const isConfirmed = r.status === 'Confirmed';
                    const isSent    = r.requester_id === profile?.record_id;
                    const photo     = isSent ? r.recipient_photo    : r.requester_photo;
                    const name      = isSent ? r.recipient_name     : r.requester_name;
                    const fonction  = isSent ? r.recipient_fonction : r.requester_fonction;
                    const company   = isSent ? r.recipient_company  : r.requester_company;
                    const logo      = isSent ? r.recipient_logo     : r.requester_logo;
                    const dateStr   = r.confirmed_slot_date
                      ? new Date(r.confirmed_slot_date).toLocaleDateString(language, { day: 'numeric', month: 'short' })
                      : '';
                    const borderCls = isCancelled ? 'border-red-100 border-l-red-400 opacity-75'
                      : isConfirmed  ? 'border-green-100 border-l-green-500'
                      : 'border-blue-100 border-l-blue-400';

                    return (
                      <div key={r.id} className={`bg-white rounded-xl shadow-sm border p-4 space-y-3 border-l-4 ${borderCls}`}>

                        {/* Ligne 1 : horaire + badge statut */}
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          {r.confirmed_slot_start ? (
                            <div className={`flex items-center gap-2 font-bold text-sm ${isCancelled ? 'text-gray-400 line-through' : isConfirmed ? 'text-green-700' : 'text-blue-700'}`}>
                              <Clock size={15} className="flex-shrink-0" />
                              <span>{t('planning.day_fallback')} {r.confirmed_slot_day}{dateStr ? ` · ${dateStr}` : ''}</span>
                              <span className="text-gray-300">·</span>
                              <span>{r.confirmed_slot_start} – {r.confirmed_slot_end || '—'}</span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2 text-sm text-gray-400 italic">
                              <Clock size={15} className="flex-shrink-0" />
                              <span>{r.status === 'Pending' ? (t('request.status_pending') || 'En attente de réponse') : (t('request.status_accepted') || "Créneau en cours d'attribution")}</span>
                            </div>
                          )}
                          {statusBadge(r.status)}
                        </div>

                        {/* Ligne 2 : table + zone */}
                        {(r.table_label || r.table_zone) && (
                          <div className="flex items-center gap-1.5 text-xs text-gray-500 bg-gray-50 rounded-full px-2.5 py-1 border border-gray-200 self-start w-fit">
                            <MapPin size={12} className="flex-shrink-0 text-gray-400" />
                            {r.table_label && <span className="font-semibold">{t('meeting_card.table')} {r.table_label}</span>}
                            {r.table_zone  && <span className="text-gray-400">— {r.table_zone}</span>}
                          </div>
                        )}

                        {/* Ligne 3 : société */}
                        <div className="flex items-center gap-2">
                          {logo
                            ? <img src={logo} alt={company} className="w-7 h-7 rounded-lg object-contain border border-gray-100 bg-gray-50 p-0.5 flex-shrink-0" />
                            : <div className="w-7 h-7 rounded-lg bg-gray-50 border border-gray-200 flex items-center justify-center flex-shrink-0"><Building2 size={12} className="text-gray-300" /></div>
                          }
                          <span className="text-xs font-bold text-gray-600 truncate">{company || '—'}</span>
                        </div>

                        {/* Ligne 4 : personne */}
                        <div className="flex items-center gap-3">
                          {photo
                            ? <img src={photo} alt={name} className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
                            : <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0"><User size={18} className="text-gray-400" /></div>
                          }
                          <div className="min-w-0">
                            <p className="text-sm font-bold text-gray-900 truncate">{name || '—'}</p>
                            {fonction && <p className="text-xs text-gray-400 truncate">{fonction}</p>}
                          </div>
                        </div>

                        {/* Message d'annulation */}
                        {isCancelled && r.response_message && (
                          <p className="text-xs text-red-500 italic bg-red-50 rounded-lg px-3 py-2 border border-red-100">
                            <span className="font-semibold not-italic">{t('planning.cancel_cancelled_msg')} </span>
                            "{r.response_message}"
                          </p>
                        )}

                        {/* Bouton annuler */}
                        {!isCancelled && (
                          <button
                            onClick={() => setCancelModal({ open: true, requestId: r.id, message: '', loading: false })}
                            className="w-full border border-red-200 text-red-500 py-2 rounded-lg text-xs font-semibold hover:bg-red-50 transition flex items-center justify-center gap-1.5"
                          >
                            <X size={13} />{t('planning.cancel_rdv')}
                          </button>
                        )}

                      </div>
                    );
                  }) : (
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-10 text-center text-gray-400">
                      <Calendar size={32} className="mx-auto mb-3 text-gray-200" />
                      <p className="text-sm">{t('planning.no_meetings')}</p>
                    </div>
                  )}

                </div>
              );
            })()}
          </div>
          );
        })()}

        {/* ===== ONGLET BADGE ===== */}
        {activeTab === 'badge' && (() => {
          const badgeConfig = contentDisplay.find(c => c.type_id === 'Badge');
          const badgeStatus = badgeConfig?.status ?? 'on';

          const isLocked = (() => {
            if (badgeStatus === 'off') return true;
            const pubDate = badgeConfig?.publication_date;
            if (!pubDate) return false;
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const pub = new Date(pubDate);
            pub.setHours(0, 0, 0, 0);
            return pub > today;
          })();

          return (
            <div className="max-w-2xl mx-auto">
              {isLocked ? (
                <div className="relative rounded-2xl overflow-hidden border border-blue-100 shadow-md bg-white">
                  <div className="absolute inset-0 opacity-5"
                    style={{ backgroundImage: 'repeating-linear-gradient(45deg, #2563eb 0, #2563eb 1px, transparent 0, transparent 50%)', backgroundSize: '12px 12px' }} />
                  <div className="relative z-10 flex flex-col items-center justify-center text-center px-6 py-10 gap-4">
                    <div className="w-14 h-14 rounded-2xl bg-blue-50 flex items-center justify-center shadow-sm">
                      <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                        <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                      </svg>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-gray-900 mb-1">
                        {badgeConfig?.message_title || 'Coming soon'}
                      </p>
                      <p className="text-sm text-gray-500 max-w-md mx-auto">
                        {badgeConfig?.message || 'Your badge will be available soon.'}
                      </p>
                      {badgeConfig?.publication_date && (
                        <p className="mt-2 inline-block bg-blue-50 text-blue-700 text-xs font-semibold px-3 py-1 rounded-full">
                          {new Date(badgeConfig.publication_date).toLocaleDateString(language === 'fr' ? 'fr-FR' : language === 'de' ? 'de-DE' : 'en-US', { day: 'numeric', month: 'long', year: 'numeric' })}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('badge.title') || 'My Badge'}</h2>
                  <p className="text-gray-600 mb-6">{t('badge.subtitle') || 'Download your digital badge for the event'}</p>
                  <BadgeSection
                    profile={profile}
                    participantId={profile?.record_id}
                    eventColor={event?.color_stripe || event?.color_main}
                    eventSlug={urlSlug || event?.slug}
                    t={t}
                    onBadgeGenerate={(badges) => {
                      console.log('Badge generated:', badges);
                    }}
                  />
                </>
              )}
            </div>
          );
        })()}

        {/* ===== ONGLET NETWORKING ===== */}
        {activeTab === 'networking' && (() => {
          const networkingConfig = contentDisplay.find(c => c.type_id === 'Networking');
          const networkingStatus = networkingConfig?.status ?? 'on';

          const isLocked = (() => {
            if (networkingStatus === 'off') return true;
            const pubDate = networkingConfig?.publication_date;
            if (!pubDate) return false;
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const pub = new Date(pubDate);
            pub.setHours(0, 0, 0, 0);
            return pub > today;
          })();

          return (
            <div className="max-w-2xl mx-auto">
              {isLocked ? (
                <div className="relative rounded-2xl overflow-hidden border border-blue-100 shadow-md bg-white">
                  <div className="absolute inset-0 opacity-5"
                    style={{ backgroundImage: 'repeating-linear-gradient(45deg, #2563eb 0, #2563eb 1px, transparent 0, transparent 50%)', backgroundSize: '12px 12px' }} />
                  <div className="relative z-10 flex flex-col items-center justify-center text-center px-6 py-10 gap-4">
                    <div className="w-14 h-14 rounded-2xl bg-blue-50 flex items-center justify-center shadow-sm">
                      <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                        <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                      </svg>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-gray-900 mb-1">
                        {networkingConfig?.message_title || 'Coming soon'}
                      </p>
                      <p className="text-sm text-gray-500 max-w-md mx-auto">
                        {networkingConfig?.message || 'Networking will be available soon.'}
                      </p>
                      {networkingConfig?.publication_date && (
                        <p className="mt-2 inline-block bg-blue-50 text-blue-700 text-xs font-semibold px-3 py-1 rounded-full">
                          {new Date(networkingConfig.publication_date).toLocaleDateString(language === 'fr' ? 'fr-FR' : language === 'de' ? 'de-DE' : 'en-US', { day: 'numeric', month: 'long', year: 'numeric' })}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  {/* Sous-onglets Participants / Connectés */}
                  {(() => {
                    const connectedCount = networkingParticipants.filter(p => p.id !== profile?.record_id && getNetConnState(p.id)?.type === 'connected').length;
                    return (
                      <div className="flex border border-gray-200 rounded-xl overflow-hidden mb-4 bg-white">
                        <button onClick={() => setNetworkingSubTab('participants')} className={`flex-1 py-2 text-xs font-semibold transition-colors ${networkingSubTab === 'participants' ? 'bg-blue-600 text-white' : 'text-gray-500 hover:text-gray-700'}`}>
                          <Users size={12} className="inline mr-1" />{t('networking.tab_participants')}
                        </button>
                        <button onClick={() => setNetworkingSubTab('connected')} className={`flex-1 py-2 text-xs font-semibold transition-colors ${networkingSubTab === 'connected' ? 'bg-blue-600 text-white' : 'text-gray-500 hover:text-gray-700'}`}>
                          <Check size={12} className="inline mr-1" />{t('networking.tab_connected')}{connectedCount > 0 && <span className="ml-1 bg-green-500 text-white rounded-full px-1.5 py-px text-[10px] font-bold">{connectedCount}</span>}
                        </button>
                      </div>
                    );
                  })()}

                  {/* Barre de recherche */}
                  <div className="relative mb-4">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      value={networkingSearch}
                      onChange={e => setNetworkingSearch(e.target.value)}
                      placeholder={t('networking.search_placeholder')}
                      className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  {networkingLoading ? (
                    <div className="flex justify-center py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                    </div>
                  ) : (() => {
                    const myId         = profile?.record_id;
                    const myNetProfile = networkingParticipants.find(p => p.id === myId);
                    const visible      = myId ? networkingParticipants.filter(p => p.id !== myId) : networkingParticipants;

                    // All unique categories, requests and company types for filters
                    const allNetCats  = [...new Set(networkingParticipants.flatMap(p => p.category || []))].sort();
                    const allNetReqs  = [...new Set(networkingParticipants.flatMap(p => p.company_requests || []))].sort();
                    const allNetTypes = [...new Set(networkingParticipants.map(p => p.company_type).filter(Boolean))].sort();

                    // Common score helper
                    const commonScore = (p) => {
                      if (!myNetProfile) return 0;
                      const myCats = new Set(myNetProfile.category || []);
                      const myReqs = new Set(myNetProfile.company_requests || []);
                      let score = 0;
                      (p.category || []).forEach(c => { if (myCats.has(c)) score++; });
                      (p.company_requests || []).forEach(r => { if (myReqs.has(r)) score++; });
                      return score;
                    };

                    const matchSearch = p => {
                      if (!networkingSearch) return true;
                      const q = networkingSearch.toLowerCase();
                      return (
                        (p.lastname + ' ' + p.firstname).toLowerCase().includes(q) ||
                        p.company?.toLowerCase().includes(q) ||
                        p.fonction?.toLowerCase().includes(q) ||
                        p.category?.some(c => c.toLowerCase().includes(q))
                      );
                    };

                    const matchFilters = p => {
                      if (netSelectedCompanyType && p.company_type !== netSelectedCompanyType) return false;
                      if (netSelectedCategories.length > 0 && !netSelectedCategories.some(c => (p.category || []).includes(c))) return false;
                      if (netSelectedRequests.length > 0  && !netSelectedRequests.some(r  => (p.company_requests || []).includes(r))) return false;
                      return true;
                    };

                    const renderNetBtn = (p) => {
                      const state = getNetConnState(p.id);
                      const busy  = networkingActionLoading[p.id];
                      if (!state) return (
                        <button onClick={() => handleNetworkingConnect(p.id)} disabled={!!busy} className="text-xs font-semibold px-3 py-1.5 rounded-full bg-blue-600 text-white hover:bg-blue-700 transition disabled:opacity-50 flex-shrink-0">
                          {busy ? '…' : t('networking.connect')}
                        </button>
                      );
                      if (state.type === 'pending_received') return (
                        <div className="flex gap-1 flex-shrink-0">
                          <button onClick={() => handleNetworkingRespond(state.connectionId, 'on', p.id)} disabled={!!busy} className="text-xs font-semibold px-2.5 py-1.5 rounded-full bg-green-600 text-white hover:bg-green-700 transition disabled:opacity-50">{busy === 'on' ? '…' : t('networking.accept')}</button>
                          <button onClick={() => handleNetworkingRespond(state.connectionId, 'decline', p.id)} disabled={!!busy} className="text-xs font-semibold px-2.5 py-1.5 rounded-full bg-red-100 text-red-600 hover:bg-red-200 transition disabled:opacity-50">{busy === 'decline' ? '…' : t('networking.decline')}</button>
                        </div>
                      );
                      if (state.type === 'pending_sent') return <span className="text-xs font-semibold px-3 py-1.5 rounded-full bg-orange-100 text-orange-600 flex-shrink-0">{t('networking.pending')}</span>;
                      if (state.type === 'connected')    return <span className="text-xs font-semibold px-3 py-1.5 rounded-full border border-green-500 text-green-600 flex-shrink-0">{t('networking.connected')}</span>;
                      if (state.type === 'declined')     return <span className="text-xs font-medium px-3 py-1.5 rounded-full bg-gray-100 text-gray-400 flex-shrink-0">{t('networking.declined')}</span>;
                      return null;
                    };

                    const renderNetCard = (p) => {
                      const state             = getNetConnState(p.id);
                      const isPendingReceived = state?.type === 'pending_received';
                      const isConnected       = state?.type === 'connected';
                      const hasContact        = isConnected && (p.email || p.phone || p.linkedin);
                      const score             = commonScore(p);
                      return (
                        <div key={p.id} className={`bg-white rounded-xl shadow-sm border p-4 flex gap-3 ${isPendingReceived ? 'border-blue-200 bg-blue-50/30' : isConnected ? 'border-green-100' : 'border-gray-100'}`}>
                          {/* Avatar cliquable → popup profil */}
                          <button onClick={() => setNetProfileModal(p)} className="flex-shrink-0 focus:outline-none" title={t('networking.view_profile')}>
                            {p.photo_thumb || p.photo ? (
                              <img src={p.photo_thumb || p.photo} alt={p.firstname} className="w-12 h-12 rounded-full object-cover hover:ring-2 hover:ring-blue-400 transition" />
                            ) : (
                              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center hover:ring-2 hover:ring-blue-400 transition">
                                <span className="text-blue-600 font-semibold text-base">{(p.lastname?.[0] || '') + (p.firstname?.[0] || '')}</span>
                              </div>
                            )}
                          </button>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0">
                                {/* Nom : NOM Prénom */}
                                <button onClick={() => setNetProfileModal(p)} className="text-left focus:outline-none">
                                  <p className="font-semibold text-gray-900 text-sm leading-tight hover:text-blue-600 transition">
                                    <span className="uppercase">{p.lastname}</span>{' '}{p.firstname}
                                  </p>
                                </button>
                                {p.fonction && <p className="text-xs text-gray-500 truncate mt-0.5">{p.fonction}</p>}
                                {p.company && (
                                  <div className="flex items-center gap-1 mt-0.5">
                                    <Building2 size={10} className="text-gray-400 flex-shrink-0" />
                                    <p className="text-xs text-gray-600 truncate">{p.company}</p>
                                  </div>
                                )}
                                {p.company_type && (
                                  <span className="inline-block text-[10px] bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded-full font-medium mt-0.5">{p.company_type}</span>
                                )}
                                {p.table && (
                                  <p className="text-[10px] text-gray-400 mt-0.5">Table : {p.table}</p>
                                )}
                              </div>
                              <div className="flex-shrink-0">{renderNetBtn(p)}</div>
                            </div>
                            {/* Categories (communes surlignées) */}
                            {p.category?.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {p.category.slice(0, 3).map((c, idx) => {
                                  const isCommon = myNetProfile && (myNetProfile.category || []).includes(c);
                                  return <span key={idx} className={`text-xs px-2 py-0.5 rounded-full ${isCommon ? 'bg-blue-600 text-white' : 'bg-blue-50 text-blue-700'}`}>{c}</span>;
                                })}
                                {p.category.length > 3 && <span className="text-xs text-gray-400 px-1 py-0.5">+{p.category.length - 3}</span>}
                              </div>
                            )}
                            {/* Company requests (communes surlignées) */}
                            {p.company_requests?.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-1">
                                {p.company_requests.slice(0, 2).map((r, idx) => {
                                  const isCommon = myNetProfile && (myNetProfile.company_requests || []).includes(r);
                                  return <span key={idx} className={`text-xs px-2 py-0.5 rounded-full ${isCommon ? 'bg-amber-500 text-white' : 'bg-amber-50 text-amber-700'}`}>{r}</span>;
                                })}
                              </div>
                            )}
                            {/* Score de points communs */}
                            {score > 0 && !isConnected && (
                              <p className="text-[10px] text-green-600 font-medium mt-1">✓ {score} {t('networking.common_points')}</p>
                            )}
                            {isPendingReceived && <p className="text-xs text-blue-600 font-medium mt-1.5">{t('networking.request_received')}</p>}
                            {hasContact && (
                              <details className="mt-2 pt-2 border-t border-gray-100">
                                <summary className="text-xs text-green-600 font-medium cursor-pointer select-none list-none hover:text-green-700">{t('networking.view_contact')}</summary>
                                <div className="mt-2 space-y-1.5">
                                  {p.email    && <a href={`mailto:${p.email}`} className="flex items-center gap-2 group"><Mail size={12} className="text-gray-400 flex-shrink-0" /><span className="text-xs text-gray-700 group-hover:text-blue-600 truncate">{p.email}</span></a>}
                                  {p.phone    && <a href={`tel:${p.phone}`} className="flex items-center gap-2 group"><Phone size={12} className="text-gray-400 flex-shrink-0" /><span className="text-xs text-gray-700 group-hover:text-blue-600">{p.phone}</span></a>}
                                  {p.linkedin && <a href={p.linkedin} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 group"><Linkedin size={12} className="text-blue-400 flex-shrink-0" /><span className="text-xs text-blue-600 group-hover:underline truncate">{p.linkedin}</span></a>}
                                </div>
                              </details>
                            )}
                          </div>
                        </div>
                      );
                    };

                    const priorityOf = p => { const s = getNetConnState(p.id); if (s?.type === 'pending_received') return 0; if (s?.type === 'pending_sent') return 1; if (s?.type === 'declined') return 4; if (s?.type === 'connected') return 3; return 2; };

                    if (networkingSubTab === 'connected') {
                      const connList = visible.filter(p => getNetConnState(p.id)?.type === 'connected' && matchSearch(p) && matchFilters(p));
                      return connList.length === 0 ? (
                        <div className="text-center py-12 text-gray-400"><Check size={36} className="mx-auto mb-3 opacity-20" /><p className="text-sm">{t('networking.no_connections')}</p></div>
                      ) : (
                        <div className="space-y-3">
                          <p className="text-xs text-gray-400">{connList.length} connecté{connList.length > 1 ? 's' : ''}</p>
                          {connList.map(p => renderNetCard(p))}
                        </div>
                      );
                    }

                    const sorted = visible
                      .filter(p => matchSearch(p) && matchFilters(p))
                      .sort((a, b) => {
                        if (netSortField !== 'default') {
                          switch (netSortField) {
                            case 'lastname':  return (a.lastname  || '').localeCompare(b.lastname  || '');
                            case 'firstname': return (a.firstname || '').localeCompare(b.firstname || '');
                            case 'company': {
                              const ac = a.company || '', bc = b.company || '';
                              if (!ac && bc) return 1;
                              if (ac && !bc) return -1;
                              return ac.localeCompare(bc);
                            }
                            case 'type':      return (a.company_type || '').localeCompare(b.company_type || '');
                            default: break;
                          }
                        }
                        const d = priorityOf(a) - priorityOf(b);
                        if (d !== 0) return d;
                        if (priorityOf(a) === 2) { const ds = commonScore(b) - commonScore(a); if (ds !== 0) return ds; }
                        return (a.lastname + a.firstname).localeCompare(b.lastname + b.firstname);
                      });

                    const hasNetFilters = netSelectedCategories.length > 0 || netSelectedRequests.length > 0 || !!netSelectedCompanyType;
                    const netActiveFilterCount = netSelectedCategories.length + netSelectedRequests.length + (netSelectedCompanyType ? 1 : 0);

                    return (
                      <>
                        {/* Barre Filtres + Tri */}
                        <div className="flex items-center gap-2 mb-3">
                          <button
                            onClick={() => setNetFiltersOpen(prev => !prev)}
                            className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-xl border transition flex-shrink-0 ${netFiltersOpen || hasNetFilters ? 'bg-blue-50 border-blue-300 text-blue-700' : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'}`}
                          >
                            <Filter size={13} />
                            {t('networking.filters') || 'Filtres'}
                            {netActiveFilterCount > 0 && (
                              <span className="bg-blue-600 text-white rounded-full px-1.5 py-px text-[10px] font-bold leading-none">{netActiveFilterCount}</span>
                            )}
                            <ChevronDown size={11} className={`transition-transform duration-200 ${netFiltersOpen ? 'rotate-180' : ''}`} />
                          </button>
                          <div className="relative flex-1">
                            <select
                              value={netSortField}
                              onChange={e => setNetSortField(e.target.value)}
                              className="w-full appearance-none bg-white border border-gray-200 rounded-xl pl-3 pr-7 py-2 text-xs font-semibold text-gray-700 focus:ring-2 focus:ring-blue-500 cursor-pointer"
                            >
                              <option value="default">{t('networking.sort_default') || 'Pertinence'}</option>
                              <option value="lastname">{t('networking.sort_lastname') || 'Nom'}</option>
                              <option value="firstname">{t('networking.sort_firstname') || 'Prénom'}</option>
                              <option value="company">{t('networking.sort_company') || 'Société'}</option>
                              <option value="type">{t('networking.sort_type') || 'Type'}</option>
                            </select>
                            <ChevronDown size={11} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                          </div>
                        </div>

                        {/* Panneau filtres collapsible */}
                        {netFiltersOpen && (
                          <div className="bg-white border border-gray-100 rounded-xl p-3 mb-3 space-y-3 shadow-sm">
                            {allNetTypes.length > 0 && (
                              <div>
                                <span className="text-[10px] text-gray-400 font-semibold uppercase tracking-wide block mb-1">{t('networking.filter_company_type') || 'Type'}</span>
                                <div className="relative">
                                  <select
                                    value={netSelectedCompanyType}
                                    onChange={e => setNetSelectedCompanyType(e.target.value)}
                                    className="w-full appearance-none bg-gray-50 border border-gray-200 rounded-lg pl-3 pr-7 py-1.5 text-xs font-medium text-gray-700 focus:ring-2 focus:ring-blue-500 cursor-pointer"
                                  >
                                    <option value="">{t('networking.filter_all_types') || 'Tous les types'}</option>
                                    {allNetTypes.map(tp => <option key={tp} value={tp}>{tp}</option>)}
                                  </select>
                                  <ChevronDown size={10} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                                </div>
                              </div>
                            )}
                            {allNetCats.length > 0 && (
                              <div>
                                <span className="text-[10px] text-gray-400 font-semibold uppercase tracking-wide block mb-1">{t('networking.filter_categories')}</span>
                                <div className="flex flex-wrap gap-1.5">
                                  {allNetCats.map(c => (
                                    <button key={c} onClick={() => setNetSelectedCategories(prev => prev.includes(c) ? prev.filter(x => x !== c) : [...prev, c])}
                                      className={`text-xs px-2.5 py-1 rounded-full border transition font-medium ${netSelectedCategories.includes(c) ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-200 hover:border-blue-300'}`}>
                                      {c}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}
                            {allNetReqs.length > 0 && (
                              <div>
                                <span className="text-[10px] text-gray-400 font-semibold uppercase tracking-wide block mb-1">{t('networking.filter_requests')}</span>
                                <div className="flex flex-wrap gap-1.5">
                                  {allNetReqs.map(r => (
                                    <button key={r} onClick={() => setNetSelectedRequests(prev => prev.includes(r) ? prev.filter(x => x !== r) : [...prev, r])}
                                      className={`text-xs px-2.5 py-1 rounded-full border transition font-medium ${netSelectedRequests.includes(r) ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-gray-600 border-gray-200 hover:border-amber-300'}`}>
                                      {r}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}
                            {hasNetFilters && (
                              <button onClick={() => { setNetSelectedCategories([]); setNetSelectedRequests([]); setNetSelectedCompanyType(''); }} className="flex items-center gap-1 text-xs text-red-400 hover:text-red-600 transition">
                                <X size={12} />{t('networking.filter_clear')}
                              </button>
                            )}
                          </div>
                        )}

                        {sorted.length === 0 ? (
                          <div className="text-center py-12 text-gray-400"><Globe size={36} className="mx-auto mb-3 opacity-30" /><p className="text-sm">{networkingSearch || hasNetFilters ? t('networking.no_results') : t('networking.no_participants')}</p></div>
                        ) : (
                          <div className="space-y-3">
                            {sorted.map(p => renderNetCard(p))}
                          </div>
                        )}

                        {/* Modal profil participant */}
                        {netProfileModal && (() => {
                          const mp        = netProfileModal;
                          const mpState   = getNetConnState(mp.id);
                          const mpConn    = mpState?.type === 'connected';
                          const mpPendRx  = mpState?.type === 'pending_received';
                          const mpPendTx  = mpState?.type === 'pending_sent';
                          const mpBusy    = networkingActionLoading[mp.id];
                          return (
                            <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={() => setNetProfileModal(null)}>
                              <div className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden flex flex-col" style={{ maxHeight: '90vh' }} onClick={e => e.stopPropagation()}>
                                {/* Header gradient */}
                                <div className="relative bg-gradient-to-br from-blue-600 to-blue-800 p-6 pb-5 flex-shrink-0">
                                  <button onClick={() => setNetProfileModal(null)} className="absolute top-4 right-4 text-white/70 hover:text-white transition rounded-full p-1 hover:bg-white/10">
                                    <X size={18} />
                                  </button>
                                  <div className="flex items-center gap-4 pr-8">
                                    {mp.photo ? (
                                      <img src={mp.photo} alt={mp.firstname} className="w-16 h-16 rounded-full object-cover border-2 border-white/40 flex-shrink-0" />
                                    ) : (
                                      <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center text-white text-2xl font-bold flex-shrink-0 select-none">
                                        {(mp.lastname?.[0] || '').toUpperCase()}
                                      </div>
                                    )}
                                    <div className="min-w-0">
                                      <h2 className="text-white font-bold text-lg leading-tight">
                                        <span className="uppercase">{mp.lastname}</span>{' '}{mp.firstname}
                                      </h2>
                                      {mp.fonction && <p className="text-blue-200 text-sm mt-0.5">{mp.fonction}</p>}
                                      {mp.company && <span className="inline-block mt-1.5 bg-white/15 text-white text-xs px-2.5 py-0.5 rounded-full">{mp.company}</span>}
                                    </div>
                                  </div>
                                </div>

                                {/* Body scrollable */}
                                <div className="overflow-y-auto flex-1 p-5 space-y-4">

                                  {/* Bouton connexion */}
                                  {!mpConn && !mpPendTx && (
                                    mpPendRx ? (
                                      <div className="flex gap-2">
                                        <button onClick={() => { handleNetworkingRespond(mpState.connectionId, 'on', mp.id); setNetProfileModal(null); }} disabled={!!mpBusy} className="flex-1 py-2.5 rounded-xl bg-green-600 text-white font-semibold text-sm hover:bg-green-700 transition disabled:opacity-50">
                                          {mpBusy ? '…' : t('networking.accept')}
                                        </button>
                                        <button onClick={() => { handleNetworkingRespond(mpState.connectionId, 'decline', mp.id); setNetProfileModal(null); }} disabled={!!mpBusy} className="flex-1 py-2.5 rounded-xl bg-red-100 text-red-600 font-semibold text-sm hover:bg-red-200 transition disabled:opacity-50">
                                          {t('networking.decline')}
                                        </button>
                                      </div>
                                    ) : (
                                      <button onClick={() => { handleNetworkingConnect(mp.id); }} disabled={!!mpBusy} className="w-full py-2.5 rounded-xl bg-blue-600 text-white font-semibold text-sm hover:bg-blue-700 transition disabled:opacity-50">
                                        {mpBusy ? '…' : t('networking.connect')}
                                      </button>
                                    )
                                  )}
                                  {mpPendTx && <div className="w-full py-2.5 rounded-xl bg-orange-50 text-orange-600 font-semibold text-sm text-center border border-orange-200">{t('networking.pending')}</div>}
                                  {mpConn   && <div className="w-full py-2.5 rounded-xl bg-green-50 text-green-700 font-semibold text-sm text-center border border-green-200 flex items-center justify-center gap-2"><Check size={15} />{t('networking.connected')}</div>}

                                  {/* Contact */}
                                  {mpConn && (mp.email || mp.phone || mp.linkedin) ? (
                                    <div className="space-y-2.5">
                                      {mp.email    && <a href={`mailto:${mp.email}`} className="flex items-center gap-3 group"><div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0 group-hover:bg-blue-100 transition"><Mail size={14} className="text-blue-500" /></div><span className="text-sm text-gray-700 group-hover:text-blue-600 truncate">{mp.email}</span></a>}
                                      {mp.phone    && <a href={`tel:${mp.phone}`} className="flex items-center gap-3 group"><div className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center flex-shrink-0 group-hover:bg-green-100 transition"><Phone size={14} className="text-green-500" /></div><span className="text-sm text-gray-700 group-hover:text-green-600">{mp.phone}</span></a>}
                                      {mp.linkedin && <a href={mp.linkedin} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 group"><div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0 group-hover:bg-blue-100 transition"><Linkedin size={14} className="text-blue-600" /></div><span className="text-sm text-blue-600 group-hover:underline">LinkedIn</span></a>}
                                    </div>
                                  ) : (mp.email || mp.phone || mp.linkedin) ? (
                                    <div className="flex items-center gap-3 text-gray-400 bg-gray-50 rounded-xl px-4 py-3">
                                      <Lock size={15} className="flex-shrink-0" />
                                      <p className="text-sm">{t('networking.contact_locked')}</p>
                                    </div>
                                  ) : null}

                                  {/* Table */}
                                  {mp.table && (
                                    <div className="flex items-center gap-3 bg-gray-50 rounded-xl px-3 py-2">
                                      <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Table</span>
                                      <span className="text-sm font-semibold text-gray-800">{mp.table}</span>
                                    </div>
                                  )}

                                  {/* Description */}
                                  {mp.description && <p className="text-sm text-gray-700 leading-relaxed bg-gray-50 rounded-xl px-3 py-2.5 whitespace-pre-wrap">{mp.description}</p>}

                                  {/* Categories */}
                                  {mp.category?.length > 0 && (
                                    <div>
                                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">{t('networking.filter_categories')}</p>
                                      <div className="flex flex-wrap gap-1.5">
                                        {mp.category.map((c, idx) => {
                                          const isCommon = myNetProfile && (myNetProfile.category || []).includes(c);
                                          return <span key={idx} className={`text-xs px-2.5 py-1 rounded-full font-medium ${isCommon ? 'bg-blue-600 text-white' : 'bg-blue-50 text-blue-700'}`}>{c}</span>;
                                        })}
                                      </div>
                                    </div>
                                  )}

                                  {/* Company requests */}
                                  {mp.company_requests?.length > 0 && (
                                    <div>
                                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">{t('networking.looking_for')}</p>
                                      <div className="flex flex-wrap gap-1.5">
                                        {mp.company_requests.map((r, idx) => {
                                          const isCommon = myNetProfile && (myNetProfile.company_requests || []).includes(r);
                                          return <span key={idx} className={`text-xs px-2.5 py-1 rounded-full font-medium border ${isCommon ? 'bg-amber-500 text-white border-amber-500' : 'bg-amber-50 text-amber-700 border-amber-100'}`}>{r}</span>;
                                        })}
                                      </div>
                                    </div>
                                  )}

                                  {/* Company */}
                                  {(mp.company || mp.company_description || mp.company_type || mp.company_activities?.length > 0 || mp.company_website) && (
                                    <div className="border-t border-gray-100 pt-4 space-y-3">
                                      <div className="flex items-center gap-3">
                                        {mp.company_logo ? (
                                          <img src={mp.company_logo} alt={mp.company} className="w-10 h-10 rounded-lg object-contain border border-gray-100 bg-white p-0.5 flex-shrink-0" />
                                        ) : (
                                          <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0"><Building2 size={18} className="text-gray-400" /></div>
                                        )}
                                        <div className="min-w-0 flex-1">
                                          <p className="font-semibold text-gray-900 text-sm">{mp.company}</p>
                                          {mp.company_type && <span className="inline-block text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full font-medium">{mp.company_type}</span>}
                                        </div>
                                        {mp.company_website && (
                                          <a href={mp.company_website.startsWith('http') ? mp.company_website : `https://${mp.company_website}`} target="_blank" rel="noopener noreferrer" className="flex-shrink-0 flex items-center gap-1 text-xs text-blue-500 hover:text-blue-700 transition">
                                            <GlobeIcon size={14} />
                                          </a>
                                        )}
                                      </div>
                                      {mp.company_description && <p className="text-sm text-gray-600 leading-relaxed bg-gray-50 rounded-xl px-3 py-2.5 whitespace-pre-wrap">{mp.company_description}</p>}
                                      {mp.company_activities?.length > 0 && (
                                        <div className="flex flex-wrap gap-1.5">
                                          {mp.company_activities.map((a, idx) => <span key={idx} className="text-xs bg-green-50 text-green-700 px-2.5 py-1 rounded-full font-medium">{a}</span>)}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })()}
                      </>
                    );
                  })()}
                </>
              )}
            </div>
          );
        })()}

        {activeTab === 'company' && (
          companyLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader className="animate-spin text-blue-600 mr-3" size={24} />
              <span className="text-gray-600">{t('company.loading')}</span>
            </div>
          ) : companyData === false ? (
            /* Pas de company trouvée — proposer d'en créer une */
            <div className="max-w-2xl mx-auto">
              <div className="bg-white rounded-xl shadow-md p-8 text-center">
                <Building2 className="mx-auto text-gray-400 mb-4" size={48} />
                <p className="text-gray-600 mb-6">{t('company.not_found')}</p>
                {companyError && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{companyError}</div>
                )}
                <button
                  onClick={handleCreateCompany}
                  disabled={companySaving}
                  className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition disabled:bg-gray-400 flex items-center justify-center gap-2 mx-auto"
                >
                  {companySaving ? <Loader className="animate-spin" size={20} /> : <Plus size={20} />}
                  {companySaving ? t('company.creating') : t('company.create')}
                </button>
              </div>
            </div>
          ) : companyData ? (
            <div className="max-w-3xl mx-auto">
              <div className="bg-white rounded-xl shadow-md p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('company.title')}</h2>

                {companyError && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{companyError}</div>
                )}
                {companySuccess && (
                  <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">{companySuccess}</div>
                )}

                <div className="space-y-5">
                  {/* Logo */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">{t('company.logo')}</label>
                    <div className="flex items-center gap-4">
                      {/* Thumbnail actuel */}
                      <div className="w-16 h-16 rounded-lg border-2 border-gray-200 bg-gray-50 flex items-center justify-center overflow-hidden flex-shrink-0">
                        {companyData.logo_thumbnail || companyData.logo ? (
                          <img
                            src={companyData.logo_thumbnail || companyData.logo}
                            alt="Logo"
                            className="w-full h-full object-contain"
                          />
                        ) : (
                          <ImageIcon className="text-gray-300" size={28} />
                        )}
                      </div>
                      {/* Bouton upload */}
                      <div>
                        <input
                          type="file"
                          id="logo-upload"
                          accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml"
                          onChange={handleLogoUpload}
                          className="hidden"
                        />
                        <label
                          htmlFor="logo-upload"
                          className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 border-dashed cursor-pointer transition text-sm font-medium
                            ${logoUploading
                              ? 'border-gray-300 text-gray-400 cursor-not-allowed'
                              : 'border-blue-400 text-blue-600 hover:bg-blue-50'}`}
                        >
                          {logoUploading
                            ? <><Loader className="animate-spin" size={16} /> {language === 'fr' ? 'Envoi...' : language === 'de' ? 'Hochladen...' : 'Uploading...'}</>
                            : <><Upload size={16} /> {t('company.upload_logo')}</>
                          }
                        </label>
                        <p className="text-xs text-gray-400 mt-1">{t('company.logo_hint')}</p>
                      </div>
                    </div>
                  </div>

                  {/* Nom de l'entreprise */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">{t('company.name')}</label>
                    <div className="relative">
                      <Building className="absolute left-3 top-3 text-gray-400" size={18} />
                      <input type="text" value={companyForm.company || ''} onChange={(e) => handleCompanyChange('company', e.target.value)}
                        className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                    </div>
                  </div>

                  {/* Type — dropdown depuis la table Types */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">{t('company.type')}</label>
                    <div className="relative">
                      <Tag className="absolute left-3 top-3 text-gray-400 pointer-events-none" size={18} />
                      <select
                        value={selectedTypeId}
                        onChange={(e) => { setSelectedTypeId(e.target.value); setCompanySuccess(''); }}
                        className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white appearance-none"
                      >
                        <option value="">{t('company.select_type')}</option>
                        {types.map(tp => (
                          <option key={tp.id} value={tp.id}>{tp.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">{t('company.description')}</label>
                    <textarea value={companyForm.description || ''} onChange={(e) => handleCompanyChange('description', e.target.value)}
                      rows="4" placeholder={t('company.description_placeholder')}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                  </div>

                  {/* Adresse */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="col-span-3">
                      <label className="block text-sm font-semibold text-gray-700 mb-1">{t('company.address')}</label>
                      <input type="text" value={companyForm.address || ''} onChange={(e) => handleCompanyChange('address', e.target.value)}
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">{t('company.zipcode')}</label>
                      <input type="text" value={companyForm.zipcode || ''} onChange={(e) => handleCompanyChange('zipcode', e.target.value)}
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">{t('company.city')}</label>
                      <input type="text" value={companyForm.city || ''} onChange={(e) => handleCompanyChange('city', e.target.value)}
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">{t('company.country')}</label>
                      <select
                        value={selectedCountryId}
                        onChange={(e) => { setSelectedCountryId(e.target.value); setCompanySuccess(''); }}
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                      >
                        <option value="">{t('company.select_country')}</option>
                        <optgroup label="— Europe —">
                          {countries
                            .filter(c => EUROPEAN_CODES.has(c.code))
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .map(c => (
                              <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                        </optgroup>
                        <optgroup label="— World —">
                          {countries
                            .filter(c => !EUROPEAN_CODES.has(c.code))
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .map(c => (
                              <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                        </optgroup>
                      </select>
                    </div>
                  </div>

                  {/* Contact */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">{t('company.email')}</label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-3 text-gray-400" size={18} />
                        <input type="email" value={companyForm.email || ''} onChange={(e) => handleCompanyChange('email', e.target.value)}
                          className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">{t('company.phone')}</label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-3 text-gray-400" size={18} />
                        <input type="tel" value={companyForm.phone || ''} onChange={(e) => handleCompanyChange('phone', e.target.value)}
                          className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">{t('company.website')}</label>
                      <div className="relative">
                        <GlobeIcon className="absolute left-3 top-3 text-gray-400" size={18} />
                        <input type="url" value={companyForm.website || ''} onChange={(e) => handleCompanyChange('website', e.target.value)}
                          placeholder="https://"
                          className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">{t('company.linkedin')}</label>
                      <div className="relative">
                        <Linkedin className="absolute left-3 top-3 text-gray-400" size={18} />
                        <input type="url" value={companyForm.linkedin || ''} onChange={(e) => handleCompanyChange('linkedin', e.target.value)}
                          placeholder="https://linkedin.com/company/..."
                          className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                      </div>
                    </div>
                  </div>

                  {/* Stand — lecture seule */}
                  {companyForm.booth && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">{t('company.booth')}</label>
                      <div className="px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-gray-700 font-medium">
                        {companyForm.booth}
                      </div>
                    </div>
                  )}

                  {/* === CHOOSE YOUR INTERESTS === */}
                  <div className="border-t pt-6 mt-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{t('company.choose_interests')}</h3>

                    {/* Main Categories */}
                    <div className="mb-6">
                      <label className="block text-sm font-semibold text-gray-700 mb-3">
                        <Layers className="inline mr-2 text-blue-600" size={18} />
                        {t('company.main_categories')}
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {categories.map(cat => (
                          <button
                            key={cat.id}
                            type="button"
                            onClick={() => toggleCategory(cat.id)}
                            className={`px-4 py-2 rounded-full text-sm font-medium border-2 transition ${
                              selectedCategories.includes(cat.id)
                                ? 'bg-blue-600 text-white border-blue-600'
                                : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400 hover:text-blue-600'
                            }`}
                          >
                            {cat.name}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Activities (grouped by category) */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-3">
                        <Tag className="inline mr-2 text-green-600" size={18} />
                        {t('company.activities')}
                      </label>
                      {selectedCategories.length === 0 ? (
                        <p className="text-sm text-gray-400 italic">
                          {language === 'fr' ? 'Sélectionnez d\'abord une catégorie ci-dessus' :
                           language === 'de' ? 'Wählen Sie zuerst eine Kategorie oben' :
                           'Select a category above first'}
                        </p>
                      ) : (
                        <div className="space-y-4">
                          {Object.entries(activitiesByCategory).map(([catName, acts]) => (
                            <div key={catName}>
                              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">{catName}</p>
                              <div className="flex flex-wrap gap-2">
                                {acts.map(act => (
                                  <button
                                    key={act.id}
                                    type="button"
                                    onClick={() => toggleActivity(act.id)}
                                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                                      selectedActivities.includes(act.id)
                                        ? 'bg-green-600 text-white border-green-600'
                                        : 'bg-white text-gray-600 border-gray-300 hover:border-green-400 hover:text-green-600'
                                    }`}
                                  >
                                    {act.name}
                                  </button>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Mes demandes */}
                  {requests.length > 0 && (
                    <div className="border-t pt-6 mt-6">
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{t('company.my_requests')}</h3>
                      <div className="flex flex-wrap gap-2">
                        {requests.map(req => (
                          <button
                            key={req.id}
                            type="button"
                            onClick={() => toggleRequest(req.id)}
                            className={`px-4 py-2 rounded-full text-sm font-medium border-2 transition ${
                              selectedRequests.includes(req.id)
                                ? 'bg-purple-600 text-white border-purple-600'
                                : 'bg-white text-gray-700 border-gray-300 hover:border-purple-400 hover:text-purple-600'
                            }`}
                          >
                            {req.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Save button */}
                  <button
                    onClick={handleCompanySave}
                    disabled={companySaving}
                    className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2 mt-4"
                  >
                    {companySaving ? (
                      <><Loader className="animate-spin" size={20} /><span>{t('company.saving')}</span></>
                    ) : (
                      <><Save size={20} /><span>{t('company.save')}</span></>
                    )}
                  </button>
                </div>
              </div>
            </div>
          ) : null
        )}

        {activeTab === 'profile' && (
          profileLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader className="animate-spin text-blue-600 mr-3" size={24} />
              <span className="text-gray-600">{t('profile.loading')}</span>
            </div>
          ) : (profileError && !profile) ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
              <p className="text-red-700">{profileError}</p>
            </div>
          ) : (
            <div className="max-w-2xl mx-auto">
              <div className="bg-white rounded-xl shadow-md p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('profile.title')}</h2>

                {profileError && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                    {profileError}
                  </div>
                )}
                {profileSuccess && (
                  <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
                    {profileSuccess}
                  </div>
                )}

                <div className="space-y-5">
                  {/* Photo de profil */}
                  <div className="flex flex-col items-center mb-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">{t('profile.photo')}</label>
                    <div className="relative group cursor-pointer">
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/gif"
                        onChange={handlePhotoUpload}
                        className="hidden"
                        id="photo-upload"
                        disabled={photoUploading}
                      />
                      <label htmlFor="photo-upload" className="cursor-pointer">
                        {profile?.profil_picture ? (
                          <div className="relative">
                            <img src={profile.profil_picture} alt="Profile" className="w-24 h-24 rounded-full object-cover border-4 border-blue-200" />
                            <div className="absolute inset-0 bg-black bg-opacity-40 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition">
                              <Camera className="text-white" size={24} />
                            </div>
                          </div>
                        ) : (
                          <div className="w-24 h-24 rounded-full bg-gray-100 border-4 border-dashed border-gray-300 flex flex-col items-center justify-center hover:border-blue-400 transition">
                            {photoUploading ? (
                              <Loader className="animate-spin text-blue-500" size={24} />
                            ) : (
                              <>
                                <Camera className="text-gray-400 mb-1" size={24} />
                                <span className="text-xs text-gray-400">{t('profile.photo_upload')}</span>
                              </>
                            )}
                          </div>
                        )}
                      </label>
                      {photoUploading && profile?.profil_picture && (
                        <div className="absolute inset-0 bg-white bg-opacity-70 rounded-full flex items-center justify-center">
                          <Loader className="animate-spin text-blue-500" size={24} />
                        </div>
                      )}
                    </div>
                    {profile?.profil_picture && !photoUploading && (
                      <label htmlFor="photo-upload" className="mt-2 text-sm text-blue-600 hover:underline cursor-pointer">
                        {t('profile.photo_change')}
                      </label>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">{t('profile.firstname')}</label>
                      <div className="relative">
                        <User className="absolute left-3 top-3 text-gray-400" size={18} />
                        <input
                          type="text"
                          value={profileForm.firstname || ''}
                          onChange={(e) => handleProfileChange('firstname', e.target.value)}
                          className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">{t('profile.lastname')}</label>
                      <div className="relative">
                        <User className="absolute left-3 top-3 text-gray-400" size={18} />
                        <input
                          type="text"
                          value={profileForm.lastname || ''}
                          onChange={(e) => handleProfileChange('lastname', e.target.value)}
                          className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">{t('profile.company')}</label>
                    <div
                      className="flex items-center gap-2 pl-10 pr-3 py-2.5 border border-gray-200 rounded-lg bg-gray-50 cursor-pointer hover:bg-gray-100 transition relative"
                      onClick={() => setActiveTab('company')}
                      title={t('company.title')}
                    >
                      <Building className="absolute left-3 top-3 text-gray-400" size={18} />
                      <span className="text-gray-700 flex-1">
                        {profile?.company_name_from_companies || companyData?.company || profile?.company || '—'}
                      </span>
                      <span className="text-xs text-blue-500 whitespace-nowrap flex items-center gap-1">
                        <ExternalLink size={11} />
                        {t('company.title')}
                      </span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">{t('profile.fonction')}</label>
                    <div className="relative">
                      <Briefcase className="absolute left-3 top-3 text-gray-400" size={18} />
                      <input
                        type="text"
                        value={profileForm.fonction || ''}
                        onChange={(e) => handleProfileChange('fonction', e.target.value)}
                        className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">{t('profile.description')}</label>
                    <textarea
                      value={profileForm.description || ''}
                      onChange={(e) => handleProfileChange('description', e.target.value)}
                      rows="4"
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder={t('profile.description_placeholder')}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">{t('profile.country')}</label>
                    <select
                      value={profileForm.country || ''}
                      onChange={(e) => handleProfileChange('country', e.target.value)}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                    >
                      <option value="">{t('company.select_country')}</option>
                      <optgroup label="— Europe —">
                        {countries
                          .filter(c => EUROPEAN_CODES.has(c.code))
                          .sort((a, b) => a.name.localeCompare(b.name))
                          .map(c => (
                            <option key={c.id} value={c.name}>{c.name}</option>
                          ))}
                      </optgroup>
                      <optgroup label="— World —">
                        {countries
                          .filter(c => !EUROPEAN_CODES.has(c.code))
                          .sort((a, b) => a.name.localeCompare(b.name))
                          .map(c => (
                            <option key={c.id} value={c.name}>{c.name}</option>
                          ))}
                      </optgroup>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">{t('profile.linkedin')}</label>
                    <div className="relative">
                      <Linkedin className="absolute left-3 top-3 text-gray-400" size={18} />
                      <input
                        type="url"
                        value={profileForm.linkedin || ''}
                        onChange={(e) => handleProfileChange('linkedin', e.target.value)}
                        placeholder="https://linkedin.com/in/votre-profil"
                        className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  {/* Téléphone */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">{t('profile.phone')}</label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-3 text-gray-400" size={18} />
                      <input
                        type="tel"
                        value={profileForm.phone || ''}
                        onChange={(e) => handleProfileChange('phone', e.target.value)}
                        placeholder="+33 6 12 34 56 78"
                        className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">{t('profile.email')}</label>
                    <input
                      type="email"
                      value={profile?.email || user?.email || ''}
                      disabled
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
                    />
                    <p className="text-xs text-gray-400 mt-1">{t('profile.email_readonly')}</p>
                  </div>

                  <button
                    onClick={handleProfileSave}
                    disabled={profileSaving}
                    className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {profileSaving ? (
                      <>
                        <Loader className="animate-spin" size={20} />
                        <span>{t('profile.saving')}</span>
                      </>
                    ) : (
                      <>
                        <Save size={20} />
                        <span>{t('profile.save')}</span>
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Lien Gérer mon compte */}
              <div className="mt-6 text-center">
                <button
                  onClick={() => { setShowAccountModal(true); setDeleteConfirmText(''); setAccountDeleteError(''); }}
                  className="text-xs text-gray-400 hover:text-gray-600 transition underline"
                >
                  {t('account.manage')}
                </button>
              </div>
            </div>
          )
        )}

        {/* ── Modale Gérer mon compte ── */}
        {showAccountModal && (
          <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
            <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => !accountDeleting && setShowAccountModal(false)} />
            <div className="relative bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl shadow-2xl overflow-hidden">
              <div className="flex justify-center pt-3 pb-1 sm:hidden">
                <div className="w-10 h-1 bg-gray-200 rounded-full" />
              </div>
              <div className="px-6 pt-4 pb-8 sm:p-6" style={{ paddingBottom: 'max(2rem, env(safe-area-inset-bottom))' }}>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-bold text-gray-900">{t('account.title')}</h2>
                  <button onClick={() => !accountDeleting && setShowAccountModal(false)} className="text-gray-400 hover:text-gray-600 p-1">
                    <X size={20} />
                  </button>
                </div>

                <div className="border border-red-200 rounded-xl p-4 bg-red-50">
                  <h3 className="text-sm font-bold text-red-700 mb-2">{t('account.delete_title')}</h3>
                  <p className="text-xs text-red-600 mb-4">{t('account.delete_description')}</p>

                  {accountDeleteError && (
                    <div className="mb-3 p-2 bg-red-100 border border-red-300 rounded-lg text-xs text-red-700">{accountDeleteError}</div>
                  )}

                  <label className="block text-xs font-semibold text-red-700 mb-1">{t('account.delete_confirm')}</label>
                  <input
                    type="text"
                    value={deleteConfirmText}
                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                    placeholder={t('account.delete_keyword')}
                    disabled={accountDeleting}
                    className="w-full px-3 py-2 border border-red-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 mb-3"
                  />

                  <div className="flex gap-2">
                    <button
                      onClick={() => setShowAccountModal(false)}
                      disabled={accountDeleting}
                      className="flex-1 py-2.5 rounded-lg text-sm font-semibold border border-gray-300 text-gray-700 hover:bg-gray-50 transition disabled:opacity-50"
                    >
                      {t('account.cancel')}
                    </button>
                    <button
                      onClick={handleDeleteAccount}
                      disabled={deleteConfirmText !== t('account.delete_keyword') || accountDeleting}
                      className="flex-1 py-2.5 rounded-lg text-sm font-semibold bg-red-600 text-white hover:bg-red-700 transition disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {accountDeleting && <Loader className="animate-spin" size={14} />}
                      {accountDeleting ? t('account.deleting') : t('account.delete_button')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Footer ── */}
        <footer className="mt-8 pb-6 text-center text-xs text-gray-400">
          <span>Powered by <span className="font-semibold">Mobivent</span></span>
          {event?.slug && (
            <>
              <span className="mx-2">·</span>
              <a
                href={`/event/${event.slug}/terms`}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-gray-600 hover:underline transition"
              >
                {t('terms.link')}
              </a>
            </>
          )}
        </footer>
      </div>

      {showRequestModal && <RequestModal />}

      {/* ── Modale Demande de RDV (inline pour éviter la perte de focus) ── */}
      {rdvModal.open && (() => {
        const { company, participants, participantsLoading, recipientId, message, sending, error, success } = rdvModal;
        return (
          <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
            <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => !sending && setRdvModal(prev => ({ ...prev, open: false }))} />
            <div className="relative bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl shadow-2xl overflow-hidden">

              {/* Header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  {company?.logo_thumbnail || company?.logo ? (
                    <img src={company.logo_thumbnail || company.logo} alt={company.name} className="w-9 h-9 rounded-lg object-contain border border-gray-100 bg-gray-50 p-0.5" />
                  ) : (
                    <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center"><Building2 size={18} className="text-blue-400" /></div>
                  )}
                  <div>
                    <p className="text-xs text-gray-400">{t('request.title')}</p>
                    <p className="text-sm font-bold text-gray-900">{company?.name}</p>
                  </div>
                </div>
                <button onClick={() => !sending && setRdvModal(prev => ({ ...prev, open: false }))} className="text-gray-400 hover:text-gray-600 p-1"><X size={20} /></button>
              </div>

              <div className="px-5 py-4 space-y-4">

                {/* Sélection interlocuteur */}
                {participantsLoading ? (
                  <div className="flex items-center gap-2 text-sm text-gray-400"><Loader size={14} className="animate-spin" />{t('request.loading_participants')}</div>
                ) : participants && participants.length > 1 ? (
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{t('request.select_recipient')}</label>
                    <div className="space-y-2">
                      {participants.map(p => (
                        <button key={p.id} onClick={() => setRdvModal(prev => ({ ...prev, recipientId: p.id }))}
                          className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 transition text-left ${recipientId === p.id ? 'border-blue-500 bg-blue-50' : 'border-gray-100 hover:border-gray-200'}`}>
                          {p.photo ? <img src={p.photo} alt={p.firstname} className="w-9 h-9 rounded-full object-cover flex-shrink-0" />
                            : <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0"><User size={16} className="text-gray-400" /></div>}
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-gray-900">{p.firstname} {p.lastname}</p>
                            {p.job_title && <p className="text-xs text-gray-400 truncate">{p.job_title}</p>}
                          </div>
                          {recipientId === p.id && <Check size={16} className="ml-auto text-blue-500 flex-shrink-0" />}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : participants && participants.length === 1 ? (
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                    {participants[0].photo ? <img src={participants[0].photo} alt={participants[0].firstname} className="w-9 h-9 rounded-full object-cover" />
                      : <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center"><User size={16} className="text-gray-400" /></div>}
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{participants[0].firstname} {participants[0].lastname}</p>
                      {participants[0].job_title && <p className="text-xs text-gray-400">{participants[0].job_title}</p>}
                    </div>
                  </div>
                ) : null}

                {/* Message */}
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{t('request.message_label')}</label>
                  <textarea
                    rows={4}
                    value={message}
                    onChange={e => setRdvModal(prev => ({ ...prev, message: e.target.value }))}
                    placeholder={t('request.message_placeholder')}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    disabled={sending || success}
                  />
                  <p className="text-xs text-gray-400 mt-1 text-right">{message.length}/500</p>
                </div>

                {error && <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{error}</p>}
                {success && (
                  <div className="flex items-center gap-2 text-green-700 bg-green-50 px-3 py-2.5 rounded-xl text-sm font-semibold">
                    <Check size={16} />{t('request.sent_success')}
                  </div>
                )}
              </div>

              {/* Footer */}
              {!success && (
                <div className="px-5 pb-5">
                  <button onClick={sendMeetingRequest} disabled={sending || !recipientId || !message.trim()}
                    className="w-full bg-blue-600 text-white py-3 rounded-xl font-semibold text-sm hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                    {sending
                      ? <><Loader size={16} className="animate-spin" />{t('request.sending')}</>
                      : <><Send size={16} />{t('request.send_button')}</>}
                  </button>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* ====== POPUP RATE LIMIT NETWORKING ====== */}
      {netRateLimitModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 text-center">
            <div className="w-14 h-14 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">⏱</span>
            </div>
            <h2 className="text-lg font-bold text-gray-900 mb-2">{t('networking.rate_limit_title')}</h2>
            <p className="text-sm text-gray-600 mb-4">{t('networking.rate_limit_msg')}</p>
            {netRateLimitCountdown > 0 && (
              <div className="mb-5">
                <p className="text-xs text-gray-500 mb-1">{t('networking.rate_limit_hint')}</p>
                <div className="text-3xl font-mono font-bold text-orange-500">
                  {String(Math.floor(netRateLimitCountdown / 60)).padStart(2, '0')}:{String(netRateLimitCountdown % 60).padStart(2, '0')}
                </div>
              </div>
            )}
            <button
              onClick={() => setNetRateLimitModal({ open: false, retryAfter: 0 })}
              className="w-full bg-gray-900 text-white py-2.5 rounded-xl font-semibold text-sm hover:bg-gray-800 transition"
            >
              {t('networking.rate_limit_ok')}
            </button>
          </div>
        </div>
      )}

      {/* ====== POPUP BIENVENUE (nouveau participant) ====== */}
      {showWelcomePopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center">
            {/* Logo événement */}
            {(event?.logo_dark || event?.logo) && (
              <div className="mb-5">
                <img
                  src={event.logo_dark || event.logo}
                  alt={event?.name}
                  className="max-h-14 object-contain mx-auto"
                />
              </div>
            )}
            {/* Titre */}
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">🎉</span>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">
              {t('welcome.title')}
            </h2>
            <p className="text-gray-600 mb-2">
              <span className="font-semibold text-blue-600">{event?.name || subEvent?.name}</span>
            </p>
            <p className="text-gray-600 mb-8">{t('welcome.message')}</p>
            {/* Bouton */}
            <button
              onClick={() => {
                setShowWelcomePopup(false);
                setActiveTab('company');
              }}
              className="w-full bg-blue-600 text-white py-3 rounded-xl font-semibold hover:bg-blue-700 transition"
            >
              {t('welcome.continue')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const App = () => (
  <LanguageProvider>
    <MatchmakingDashboard />
  </LanguageProvider>
);

export default App;